import { db } from "./dbClient";

// ─── Trainingpage.jsx ────────────────────────────────────────────────────────
// Training Centre with:
//  • Preloaded AML/Fraud/Compliance modules (fully editable)
//  • Upload custom PPTX (parsed slide-by-slide via text extraction)
//  • Add/edit/delete slides and quiz questions on any module
//  • AI assistant that can rewrite slides, add questions, improve content
//  • Certificates on completion

import { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { useAITracker } from "./useAITracker";
import { useAuditLog } from "./useAuditLog";
import Papa from "papaparse";

// ─── PRELOADED TRAINING DATA ──────────────────────────────────────────────────
const PRELOADED_TRAININGS = [
  {
    id: "aml-foundations",
    category: "AML",
    title: "AML Fundamentals",
    subtitle: "Anti-Money Laundering Core Concepts",
    duration: "45 min",
    level: "Foundation",
    color: "#FF4444",
    icon: "⬡",
    description: "Master the fundamentals of Anti-Money Laundering: the three stages of money laundering, regulatory obligations, and your role in preventing financial crime.",
    isPreloaded: true,
    slides: [
      { title: "Anti-Money Laundering Fundamentals", subtitle: "Understanding Your Role in Preventing Financial Crime", type: "title", bg: "0A1628", accent: "00D4FF" },
      { title: "What is Money Laundering?", type: "content", body: "Money laundering is the process of making illegally obtained money appear legitimate. It allows criminals to enjoy proceeds of crime without arousing suspicion.", bullets: ["Estimated $800B–$2T laundered globally each year (2–5% of global GDP)", "Funds organised crime, terrorism, corruption and trafficking", "Financial institutions are the primary gatekeepers against laundering", "Failure to prevent it carries severe criminal and regulatory penalties"], bg: "111827", accent: "FF4444" },
      { title: "The Three Stages of Money Laundering", type: "three-col", cols: [{ heading: "1. Placement", icon: "↓", body: "Introducing criminal proceeds into the financial system. The riskiest stage — cash deposits, structuring, smurfing." }, { heading: "2. Layering", icon: "⟳", body: "Disguising the trail through complex transactions. Wire transfers, shell companies, currency conversions." }, { heading: "3. Integration", icon: "↑", body: "Re-entering the economy as 'clean' money. Real estate, luxury goods, business investments." }], bg: "111827", accent: "00D4FF" },
      { title: "Key Regulatory Framework", type: "content", body: "AML obligations are set by international standards and local regulations that all financial institutions must follow.", bullets: ["FATF 40 Recommendations — the global AML/CFT standard", "Bank Secrecy Act (BSA) — US reporting and recordkeeping", "EU Anti-Money Laundering Directives (4AMLD, 5AMLD, 6AMLD)", "UK Proceeds of Crime Act 2002 & Money Laundering Regulations 2017", "Failure to comply: unlimited fines, licence revocation, criminal prosecution"], bg: "111827", accent: "F0B429" },
      { title: "Know Your Customer (KYC)", type: "content", body: "KYC is the cornerstone of AML compliance. You must verify who your customers are before and during a business relationship.", bullets: ["Customer Identification Program (CIP): name, DOB, address, ID number", "Customer Due Diligence (CDD): understand the nature and purpose of the relationship", "Enhanced Due Diligence (EDD): required for high-risk customers including PEPs", "Ongoing monitoring: transaction patterns must match expected customer profile"], bg: "111827", accent: "00E676" },
      { title: "Suspicious Activity Reporting", type: "content", body: "When you suspect money laundering, you have a legal obligation to report it internally to your MLRO.", bullets: ["Internal SAR must be filed with your MLRO immediately upon suspicion", "MLRO has 7 days to decide whether to file an external report (FIU/FinCEN)", "Tipping-off prohibition: NEVER tell the customer a SAR has been filed", "You are protected from liability if you file in good faith", "'No suspicion' is not a defence — wilful blindness is an offence"], bg: "111827", accent: "FF8C00" },
      { title: "Red Flags", type: "content", body: "Red flags are indicators that a transaction or customer behaviour may be suspicious.", bullets: ["Structuring: multiple cash deposits just below reporting thresholds", "Unusual geography: transfers to/from high-risk jurisdictions", "PEP involvement: current or former government officials", "Beneficial ownership opacity: customer unwilling to reveal who controls funds", "No business rationale: transactions with no clear commercial purpose", "Rapid movement of funds: funds moving in and out within hours"], bg: "111827", accent: "FF4444" },
      { title: "Your Obligations", type: "content", body: "Every employee has personal obligations under AML law — not just the compliance team.", bullets: ["Report suspicions to your MLRO via the internal SAR process immediately", "Never tip off a customer that they are under investigation", "Cooperate fully with investigations", "Complete AML training annually and when regulations change"], bg: "0A1628", accent: "00D4FF" },
    ],
    quiz: [
      { q: "What are the three stages of money laundering in the correct order?", options: ["Placement, Integration, Layering", "Layering, Placement, Integration", "Placement, Layering, Integration", "Integration, Layering, Placement"], answer: 2, explanation: "Placement (introducing funds), Layering (disguising the trail), Integration (re-entering the economy)." },
      { q: "What does the 'tipping-off' prohibition mean?", options: ["You must tip off your manager before filing a SAR", "You must never inform a subject that a SAR has been filed or that they are under investigation", "You should tip off law enforcement before the MLRO", "You must warn customers about high-risk transactions"], answer: 1, explanation: "Tipping off is a criminal offence. Telling a subject a SAR has been filed can undermine the investigation." },
      { q: "Which global body sets the 40 Recommendations that form the international AML standard?", options: ["FinCEN", "Interpol", "FATF (Financial Action Task Force)", "The World Bank"], answer: 2, explanation: "The Financial Action Task Force (FATF) develops the international AML/CFT standards known as the 40 Recommendations." },
      { q: "What is 'structuring' (also known as smurfing)?", options: ["Creating complex corporate structures to hide ownership", "Breaking large cash amounts into smaller deposits to avoid reporting thresholds", "Using multiple currencies to layer funds", "Setting up front companies for integration"], answer: 1, explanation: "Structuring is breaking large sums into smaller deposits to stay below regulatory reporting thresholds — it is itself a criminal offence." },
      { q: "When must you file an internal Suspicious Activity Report?", options: ["Only when you are certain a crime has been committed", "When a transaction exceeds $10,000", "Immediately when you form a suspicion — certainty is not required", "Only at year-end compliance reviews"], answer: 2, explanation: "You must file an internal SAR as soon as you form a suspicion. Waiting for certainty can itself constitute an offence." },
    ],
  },
  {
    id: "fraud-awareness",
    category: "Fraud",
    title: "Fraud Awareness",
    subtitle: "Detecting & Preventing Financial Fraud",
    duration: "40 min",
    level: "Foundation",
    color: "#FF8C00",
    icon: "◈",
    description: "Learn to identify the most common fraud typologies, internal fraud red flags, and your responsibilities when you suspect fraudulent activity.",
    isPreloaded: true,
    slides: [
      { title: "Fraud Awareness Training", subtitle: "Protecting Your Organisation from Financial Fraud", type: "title", bg: "1A0E0A", accent: "FF8C00" },
      { title: "What is Financial Fraud?", type: "content", body: "Fraud is an intentional act of deception for financial gain. It differs from money laundering in that it is the predicate offence.", bullets: ["Global fraud losses estimated at $5.1 trillion annually", "Fraud can be committed by customers, third parties, or employees", "Ranges from payment fraud to identity theft to sophisticated cyber fraud", "Financial institutions face both direct losses and regulatory sanctions"], bg: "111827", accent: "FF8C00" },
      { title: "Common Fraud Typologies", type: "three-col", cols: [{ heading: "Payment Fraud", icon: "💳", body: "Unauthorised transactions, card-not-present fraud, account takeover, push payment (APP) fraud." }, { heading: "Identity Fraud", icon: "👤", body: "Using stolen or synthetic identities to open accounts, obtain credit, or make fraudulent claims." }, { heading: "Internal Fraud", icon: "⚠", body: "Embezzlement, expense fraud, payroll manipulation, or collusion with external parties by employees." }], bg: "111827", accent: "FF8C00" },
      { title: "Authorised Push Payment (APP) Fraud", type: "content", body: "APP fraud is now the most prevalent form of bank fraud. Victims are tricked into authorising payments to fraudsters.", bullets: ["Invoice fraud: intercepting supplier emails and changing payment details", "CEO fraud: impersonating senior executives to authorise urgent payments", "Romance scams: building online relationships to request money transfers", "Investment fraud: fake platforms promising unrealistic returns", "Impersonation: posing as HMRC, banks, or police"], bg: "111827", accent: "FF4444" },
      { title: "The Fraud Triangle", type: "three-col", cols: [{ heading: "Pressure", icon: "↓", body: "Personal financial problems, debt, lifestyle pressure, or workplace grievances that motivate fraud." }, { heading: "Opportunity", icon: "🔓", body: "Weak controls, lack of oversight, trust without verification, or system access that enables fraud." }, { heading: "Rationalisation", icon: "💭", body: "Justifying the behaviour — 'I'll pay it back,' 'I'm underpaid,' 'The company won't miss it.'" }], bg: "111827", accent: "FF8C00" },
      { title: "Your Responsibilities", type: "content", body: "Everyone has a role in preventing fraud. Your actions and alertness are the first line of defence.", bullets: ["Report suspected fraud immediately to your line manager and fraud team", "Never override security controls — even if pressured by a senior colleague", "Verify all payment instruction changes via a known, confirmed telephone number", "Protect your credentials — never share passwords, tokens, or access cards"], bg: "1A0E0A", accent: "FF8C00" },
    ],
    quiz: [
      { q: "What is Authorised Push Payment (APP) fraud?", options: ["Unauthorised use of someone's card details", "Tricking victims into willingly transferring money to a fraudster", "Internal embezzlement by employees", "Hacking into banking systems to divert funds"], answer: 1, explanation: "APP fraud involves deceiving victims into authorising payments themselves — such as through invoice fraud, CEO fraud, or impersonation scams." },
      { q: "What are the three elements of the Fraud Triangle?", options: ["Greed, Deception, Concealment", "Pressure, Opportunity, Rationalisation", "Motive, Means, Opportunity", "Access, Intent, Cover-up"], answer: 1, explanation: "The Fraud Triangle identifies: Pressure (motivation), Opportunity (ability to commit), and Rationalisation (justification)." },
      { q: "Which of these is a red flag for potential internal fraud?", options: ["An employee who always takes their full holiday entitlement", "An employee who requests dual authorisation for payments", "An employee who refuses to take holidays and insists on handling processes alone", "An employee who reports suspected fraud to their manager"], answer: 2, explanation: "Refusing to take holidays and insisting on sole control of processes is a classic red flag — the employee fears detection while absent." },
    ],
  },
  {
    id: "compliance-essentials",
    category: "Compliance",
    title: "Compliance Essentials",
    subtitle: "Regulatory Obligations & Governance",
    duration: "50 min",
    level: "Foundation",
    color: "#00D4FF",
    icon: "◉",
    description: "A comprehensive overview of compliance obligations, governance frameworks, sanctions screening, data protection, and building a strong compliance culture.",
    isPreloaded: true,
    slides: [
      { title: "Compliance Essentials", subtitle: "Regulatory Obligations, Governance & Culture", type: "title", bg: "0A1628", accent: "00D4FF" },
      { title: "What is Compliance?", type: "content", body: "Compliance means adhering to laws, regulations, policies, and ethical standards. It protects the firm, its customers, and the wider financial system.", bullets: ["Regulatory compliance: meeting legal obligations set by regulators (FCA, FinCEN, MAS)", "Internal compliance: following company policies and procedures", "Ethics & conduct: acting with integrity and in customers' best interests", "Non-compliance costs: fines, sanctions, reputational damage, criminal prosecution"], bg: "111827", accent: "00D4FF" },
      { title: "Sanctions Compliance", type: "content", body: "Sanctions are restrictions imposed on individuals, entities, and countries. Breaching sanctions is a criminal offence.", bullets: ["Key lists: OFAC SDN (US), UN Security Council, EU Consolidated List, HMT (UK)", "All customers and transactions must be screened before processing", "Designated persons: cannot transact, must freeze assets and report", "Penalties: up to $1 million per violation, criminal prosecution, asset seizure"], bg: "111827", accent: "FF4444" },
      { title: "Data Protection & Privacy", type: "content", body: "GDPR and equivalent laws impose strict obligations on how we collect, process, and protect personal data.", bullets: ["Lawful basis: personal data may only be processed with a legal basis", "Data minimisation: collect only what is necessary", "Subject rights: individuals can request access, correction, or deletion", "Breach notification: must report to regulator within 72 hours", "Penalties: up to €20M or 4% of global annual turnover"], bg: "111827", accent: "F0B429" },
      { title: "Whistleblowing", type: "content", body: "Whistleblowing protections exist to encourage employees to report wrongdoing without fear of retaliation.", bullets: ["You have the right to report concerns internally or directly to the regulator", "Qualifying disclosures are protected by law", "Anonymous reporting channels must be available in regulated firms", "Retaliation against whistleblowers is a regulatory and employment law breach"], bg: "111827", accent: "00D4FF" },
      { title: "Building a Compliance Culture", type: "content", body: "Compliance is most effective when it is embedded in culture — not just policy documents.", bullets: ["Tone from the top: leadership must model compliant behaviour", "Speak up: raise concerns early without fear of repercussions", "Complete all mandatory training on time, every time", "If in doubt, ask Compliance before acting — not after"], bg: "0A1628", accent: "00D4FF" },
    ],
    quiz: [
      { q: "What is the maximum GDPR fine for a serious breach?", options: ["£500,000", "€10 million", "€20 million or 4% of global annual turnover (whichever is higher)", "€5 million or 2% of global annual turnover"], answer: 2, explanation: "Under GDPR, the maximum fine for the most serious violations is €20 million or 4% of global annual turnover — whichever is higher." },
      { q: "Which of the following is NOT a sanctions list you should be aware of?", options: ["OFAC SDN List (US)", "UN Security Council Consolidated List", "INTERPOL Red Notice List", "HMT Consolidated List (UK)"], answer: 2, explanation: "INTERPOL Red Notices are not a sanctions list — they are requests to locate and arrest individuals." },
      { q: "Within what timeframe must a personal data breach be reported to the regulator under GDPR?", options: ["24 hours", "72 hours", "7 days", "30 days"], answer: 1, explanation: "Under GDPR Article 33, a personal data breach must be notified to the supervisory authority within 72 hours of becoming aware." },
      { q: "You observe a colleague bypassing an approval control to process a payment. What should you do?", options: ["Say nothing — it's probably a one-off", "Help them do it faster next time", "Report it through the firm's whistleblowing or internal reporting channel", "Ask the colleague informally and take their word for it"], answer: 2, explanation: "Bypassing controls is a serious compliance concern. Report via whistleblowing or internal reporting. You are protected from retaliation." },
    ],
  },
];

// ─── CERTIFICATE ──────────────────────────────────────────────────────────────
function downloadCertificate(trainingTitle, userName, completedAt, companyName) {
  const date = completedAt
    ? new Date(completedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const certId = `CRG-${Date.now().toString(36).toUpperCase()}`;
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Certificate — ${trainingTitle}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&family=DM+Mono:wght@300;400;500&display=swap');
  *{margin:0;padding:0;box-sizing:border-box}
  body{width:297mm;height:210mm;background:#fff;display:flex;align-items:center;justify-content:center;font-family:'DM Mono',monospace}
  .cert{width:100%;height:100%;padding:18mm 20mm;position:relative;border:12px solid #0A2342;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center}
  .cert::before{content:'';position:absolute;inset:8px;border:2px solid #00D4FF;pointer-events:none}
  .logo{display:flex;align-items:center;gap:10px;margin-bottom:6mm}
  .logo-hex{width:32px;height:32px;background:#00D4FF;clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)}
  .logo-text{font-family:'Syne',sans-serif;font-weight:800;font-size:22px;color:#0A2342;letter-spacing:0.04em}
  .subtitle{font-size:9px;letter-spacing:0.2em;color:#4A5568;margin-bottom:6mm}
  .cert-title{font-family:'Syne',sans-serif;font-size:11px;letter-spacing:0.25em;color:#00D4FF;margin-bottom:4mm}
  .completion{font-family:'Syne',sans-serif;font-size:32px;font-weight:800;color:#0A2342;margin-bottom:5mm;line-height:1.2}
  .presented{font-size:11px;color:#4A5568;margin-bottom:3mm}
  .name{font-family:'Syne',sans-serif;font-size:26px;font-weight:700;color:#0A2342;border-bottom:2px solid #00D4FF;padding-bottom:3mm;margin-bottom:3mm;min-width:120mm}
  .company{font-size:11px;color:#4A5568;margin-bottom:6mm}
  .module{font-size:10px;color:#4A5568;margin-bottom:2mm}
  .module-name{font-family:'Syne',sans-serif;font-size:16px;font-weight:700;color:#0A2342;margin-bottom:6mm}
  .footer{display:flex;justify-content:space-between;width:100%;margin-top:auto;align-items:flex-end}
  .sig-block{text-align:center}
  .sig-line{width:50mm;border-top:1px solid #0A2342;margin-bottom:2mm}
  .sig-label{font-size:9px;color:#4A5568;letter-spacing:0.1em}
  .cert-id{font-size:8px;color:#8A9BB5}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body><div class="cert">
  <div class="logo"><div class="logo-hex"></div><span class="logo-text">CrossRegGuard</span></div>
  <div class="subtitle">COMPLIANCE OPERATING SYSTEM · BY CROSSREG NEXUS</div>
  <div class="cert-title">CERTIFICATE OF COMPLETION</div>
  <div class="completion">This certifies that</div>
  <div class="presented">the following individual has successfully completed</div>
  <div class="name">${userName}</div>
  <div class="company">${companyName || "CrossReg Nexus"}</div>
  <div class="module">the compliance training module</div>
  <div class="module-name">${trainingTitle}</div>
  <div class="footer">
    <div class="sig-block"><div class="sig-line"></div><div class="sig-label">DATE OF COMPLETION</div><div class="sig-label" style="font-weight:700;color:#0A2342">${date}</div></div>
    <div class="cert-id">Certificate ID: ${certId}</div>
    <div class="sig-block"><div class="sig-line"></div><div class="sig-label">AUTHORISED BY</div><div class="sig-label" style="font-weight:700;color:#0A2342">CrossReg Nexus</div></div>
  </div>
</div></body></html>`;
  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 800); }
}

// ─── SLIDE VIEWER (read-only mode for taking training) ────────────────────────
function SlideViewer({ slides, onComplete, completed }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const slide = slides[current];
  const isLast = current === slides.length - 1;
  const bg = slide.bg || "111827";
  const accent = slide.accent || "00D4FF";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ flex: 1, background: `#${bg}`, borderRadius: 12, overflow: "hidden",
        position: "relative", minHeight: 380, display: "flex", flexDirection: "column",
        justifyContent: "center", padding: "40px 52px", border: `1px solid ${C.border}` }}>
        <div style={{ position: "absolute", top: 16, right: 20, display: "flex", gap: 5 }}>
          {slides.map((_, i) => (
            <div key={i} onClick={() => setCurrent(i)} style={{ width: i === current ? 18 : 7, height: 7, borderRadius: 4, background: i === current ? `#${accent}` : i < current ? `#${accent}88` : "#ffffff22", cursor: "pointer", transition: "all 0.2s" }} />
          ))}
        </div>
        <div style={{ position: "absolute", top: 16, left: 20, color: "#ffffff44", fontSize: 11, fontFamily: "'DM Mono',monospace" }}>{current + 1} / {slides.length}</div>

        {slide.type === "title" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 60, height: 4, background: `#${accent}`, borderRadius: 2, margin: "0 auto 28px" }} />
            <div style={{ color: `#${accent}`, fontSize: 12, letterSpacing: "0.2em", fontFamily: "'DM Mono',monospace", marginBottom: 16, textTransform: "uppercase" }}>Training Module</div>
            <div style={{ color: "#ffffff", fontFamily: "'Syne',sans-serif", fontSize: 32, fontWeight: 800, lineHeight: 1.2, marginBottom: 16 }}>{slide.title}</div>
            {slide.subtitle && <div style={{ color: "#ffffff99", fontSize: 16, lineHeight: 1.5 }}>{slide.subtitle}</div>}
            <div style={{ width: 60, height: 4, background: `#${accent}`, borderRadius: 2, margin: "28px auto 0" }} />
          </div>
        )}

        {(slide.type === "content" || slide.type === "custom") && (
          <div>
            <div style={{ color: `#${accent}`, fontSize: 11, letterSpacing: "0.12em", fontFamily: "'DM Mono',monospace", marginBottom: 12, textTransform: "uppercase" }}>{slide.title}</div>
            <div style={{ width: 40, height: 3, background: `#${accent}`, borderRadius: 2, marginBottom: 20 }} />
            {slide.body && <div style={{ color: "#ffffffcc", fontSize: 15, lineHeight: 1.7, marginBottom: 22 }}>{slide.body}</div>}
            {slide.bullets?.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {slide.bullets.map((b, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: `#${accent}`, flexShrink: 0, marginTop: 7 }} />
                    <div style={{ color: "#ffffffbb", fontSize: 14, lineHeight: 1.6 }}>{b}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {slide.type === "three-col" && (
          <div>
            <div style={{ color: `#${accent}`, fontSize: 11, letterSpacing: "0.12em", fontFamily: "'DM Mono',monospace", marginBottom: 12, textTransform: "uppercase" }}>{slide.title}</div>
            <div style={{ width: 40, height: 3, background: `#${accent}`, borderRadius: 2, marginBottom: 24 }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
              {slide.cols?.map((col, i) => (
                <div key={i} style={{ background: "#ffffff0a", border: `1px solid #${accent}33`, borderRadius: 10, padding: "20px 18px" }}>
                  <div style={{ color: `#${accent}`, fontSize: 22, marginBottom: 10 }}>{col.icon}</div>
                  <div style={{ color: "#ffffff", fontSize: 15, fontWeight: 700, fontFamily: "'Syne',sans-serif", marginBottom: 10 }}>{col.heading}</div>
                  <div style={{ color: "#ffffff99", fontSize: 13, lineHeight: 1.6 }}>{col.body}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
        <button onClick={() => setCurrent(p => Math.max(0, p - 1))} disabled={current === 0}
          style={{ padding: "8px 18px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: current === 0 ? C.textDim : C.textMid, cursor: current === 0 ? "default" : "pointer", fontFamily: "'DM Mono',monospace", fontSize: 13 }}>
          {t('training.prevSlide')}
        </button>
        <div style={{ color: C.textDim, fontSize: 12 }}>{Math.round(((current + 1) / slides.length) * 100)}{t('training.pctComplete')}</div>
        {isLast ? (
          <button onClick={onComplete}
            style={{ padding: "8px 22px", background: C.success, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 13 }}>
            {completed ? t('training.slidesReviewed') : t('training.completeSlides')}
          </button>
        ) : (
          <button onClick={() => setCurrent(p => p + 1)}
            style={{ padding: "8px 18px", background: C.accent, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 13 }}>
            {t('training.nextSlide')}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── QUIZ ─────────────────────────────────────────────────────────────────────
function Quiz({ questions, onComplete, passed }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [answers,   setAnswers]   = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [current,   setCurrent]   = useState(0);

  const q     = questions[current];
  const isLast = current === questions.length - 1;
  const score  = submitted ? questions.filter((q, i) => answers[i] === q.answer).length : 0;
  const pct    = submitted ? Math.round((score / questions.length) * 100) : 0;
  const didPass = pct >= 80;

  function submit() {
    if (Object.keys(answers).length < questions.length) return;
    const s = questions.filter((q, i) => answers[i] === q.answer).length;
    setSubmitted(true);
    if (Math.round((s / questions.length) * 100) >= 80) onComplete();
  }

  function reset() { setAnswers({}); setSubmitted(false); setCurrent(0); }

  if (submitted) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 20px", gap: 20, textAlign: "center" }}>
      <div style={{ width: 80, height: 80, borderRadius: "50%", background: didPass ? "rgba(0,230,118,0.1)" : "rgba(255,68,68,0.1)", border: `2px solid ${didPass ? C.success : C.danger}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>{didPass ? "✓" : "✕"}</div>
      <div style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 700 }}>{didPass ? t('training.quizPassed') : t('training.quizFailed')}</div>
      <div style={{ color: didPass ? C.success : C.danger, fontSize: 32, fontWeight: 800, fontFamily: "'DM Mono',monospace" }}>{pct}%</div>
      <div style={{ color: C.textMid, fontSize: 14 }}>{t('training.scoreOf', { score, total: questions.length })} · {didPass ? t('training.passMarkAchieved') : t('training.passMarkRequired')}</div>
      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
        {questions.map((q, i) => {
          const correct = answers[i] === q.answer;
          return (
            <div key={i} style={{ background: C.bg, border: `1px solid ${correct ? "rgba(0,230,118,0.3)" : "rgba(255,68,68,0.3)"}`, borderRadius: 8, padding: "14px 16px", textAlign: "left" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6 }}>
                <span style={{ color: correct ? C.success : C.danger, fontSize: 14, flexShrink: 0 }}>{correct ? "✓" : "✕"}</span>
                <div style={{ color: C.text, fontSize: 13 }}>{q.q}</div>
              </div>
              {!correct && <div style={{ color: C.text, fontSize: 12, marginLeft: 22, marginTop: 4 }}><span style={{ color: C.success }}>{t('training.correct')}: </span>{q.options[q.answer]}</div>}
              <div style={{ color: C.textDim, fontSize: 12, marginLeft: 22, marginTop: 6, lineHeight: 1.6 }}>{q.explanation}</div>
            </div>
          );
        })}
      </div>
      {!didPass && <button onClick={reset} style={{ padding: "10px 24px", background: C.accent, border: "none", borderRadius: 8, color: C.bg, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 13 }}>{t('training.retakeQuiz')}</button>}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ color: C.textMid, fontSize: 13 }}>{t('training.questionOf', { current: current + 1, total: questions.length })}</div>
        <div style={{ display: "flex", gap: 5 }}>
          {questions.map((_, i) => (
            <div key={i} onClick={() => setCurrent(i)} style={{ width: 24, height: 5, borderRadius: 3, background: answers[i] !== undefined ? C.accent : i === current ? C.borderLight : C.border, cursor: "pointer" }} />
          ))}
        </div>
      </div>
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "22px 24px" }}>
        <div style={{ color: C.text, fontSize: 16, lineHeight: 1.6, fontWeight: 600, marginBottom: 20 }}>{q.q}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {q.options.map((opt, i) => {
            const selected = answers[current] === i;
            return (
              <button key={i} onClick={() => setAnswers(p => ({ ...p, [current]: i }))}
                style={{ textAlign: "left", padding: "12px 16px", borderRadius: 8, cursor: "pointer", background: selected ? C.accentGlow : "transparent", border: `1px solid ${selected ? C.accent : C.border}`, color: selected ? C.accent : C.text, fontSize: 14, fontFamily: "'DM Mono',monospace", lineHeight: 1.5, transition: "all 0.13s" }}>
                <span style={{ color: selected ? C.accent : C.textMid, marginRight: 10, fontWeight: 700 }}>{String.fromCharCode(65 + i)}.</span>{opt}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <button onClick={() => setCurrent(p => Math.max(0, p - 1))} disabled={current === 0}
          style={{ padding: "8px 18px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: current === 0 ? C.textDim : C.textMid, cursor: current === 0 ? "default" : "pointer", fontFamily: "'DM Mono',monospace", fontSize: 13 }}>
          {t('training.back')}
        </button>
        {isLast ? (
          <button onClick={submit} disabled={Object.keys(answers).length < questions.length}
            style={{ padding: "8px 22px", background: Object.keys(answers).length >= questions.length ? C.accent : C.border, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: Object.keys(answers).length >= questions.length ? "pointer" : "default", fontFamily: "'DM Mono',monospace", fontSize: 13 }}>
            {t('training.submitQuiz')}
          </button>
        ) : (
          <button onClick={() => setCurrent(p => p + 1)} disabled={answers[current] === undefined}
            style={{ padding: "8px 18px", background: answers[current] !== undefined ? C.accent : C.border, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: answers[current] !== undefined ? "pointer" : "default", fontFamily: "'DM Mono',monospace", fontSize: 13 }}>
            {t('training.nextSlide')}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── SLIDE EDITOR ─────────────────────────────────────────────────────────────
function SlideEditor({ slides, onChange }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [editIdx, setEditIdx] = useState(null);

  function updateSlide(idx, field, value) {
    const updated = slides.map((s, i) => i === idx ? { ...s, [field]: value } : s);
    onChange(updated);
  }

  function updateBullet(idx, bi, value) {
    const updated = slides.map((s, i) => {
      if (i !== idx) return s;
      const bullets = [...(s.bullets || [])];
      bullets[bi] = value;
      return { ...s, bullets };
    });
    onChange(updated);
  }

  function addBullet(idx) {
    const updated = slides.map((s, i) => i === idx ? { ...s, bullets: [...(s.bullets || []), "New bullet point"] } : s);
    onChange(updated);
  }

  function removeBullet(idx, bi) {
    const updated = slides.map((s, i) => i === idx ? { ...s, bullets: (s.bullets || []).filter((_, j) => j !== bi) } : s);
    onChange(updated);
  }

  function addSlide() {
    onChange([...slides, { title: "New Slide", type: "content", body: "", bullets: [], bg: "111827", accent: "00D4FF" }]);
    setEditIdx(slides.length);
  }

  function removeSlide(idx) {
    if (slides.length <= 1) return;
    const updated = slides.filter((_, i) => i !== idx);
    onChange(updated);
    setEditIdx(null);
  }

  function moveSlide(idx, dir) {
    const updated = [...slides];
    const target = idx + dir;
    if (target < 0 || target >= updated.length) return;
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    onChange(updated);
    setEditIdx(target);
  }

  const inp = { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "7px 10px", fontFamily: "'DM Mono',monospace", outline: "none", width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ display: "flex", gap: 16 }}>
      {/* Slide list */}
      <div style={{ width: 200, flexShrink: 0 }}>
        <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", marginBottom: 8 }}>{t('training.slidesCount', { count: slides.length })}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {slides.map((s, i) => (
            <button key={i} onClick={() => setEditIdx(i)}
              style={{ textAlign: "left", padding: "8px 10px", borderRadius: 7, background: editIdx === i ? C.accentGlow : "transparent", border: `1px solid ${editIdx === i ? C.accent : C.border}`, color: editIdx === i ? C.accent : C.textMid, fontSize: 11, fontFamily: "'DM Mono',monospace", cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {i + 1}. {s.title || "Untitled"}
            </button>
          ))}
          <button onClick={addSlide}
            style={{ padding: "8px 10px", borderRadius: 7, background: "transparent", border: `1px dashed ${C.borderLight}`, color: C.textDim, fontSize: 11, cursor: "pointer", fontFamily: "'DM Mono',monospace" }}>
            {t('training.addSlide')}
          </button>
        </div>
      </div>

      {/* Edit panel */}
      <div style={{ flex: 1 }}>
        {editIdx === null ? (
          <div style={{ color: C.textDim, fontSize: 13, padding: "40px 0", textAlign: "center" }}>
            {t('training.selectSlideToEdit')}
          </div>
        ) : (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{t('training.slideN', { n: editIdx + 1 })}</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => moveSlide(editIdx, -1)} disabled={editIdx === 0}
                  style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 5, color: C.textDim, cursor: editIdx === 0 ? "default" : "pointer", fontSize: 12 }}>↑</button>
                <button onClick={() => moveSlide(editIdx, 1)} disabled={editIdx === slides.length - 1}
                  style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 5, color: C.textDim, cursor: editIdx === slides.length - 1 ? "default" : "pointer", fontSize: 12 }}>↓</button>
                <button onClick={() => removeSlide(editIdx)}
                  style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${C.danger}40`, borderRadius: 5, color: C.danger, cursor: "pointer", fontSize: 11 }}>{t('training.delete')}</button>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div>
                <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>{t('training.type')}</div>
                <select value={slides[editIdx].type} onChange={e => updateSlide(editIdx, "type", e.target.value)} style={inp}>
                  <option value="title">Title</option>
                  <option value="content">Content</option>
                  <option value="three-col">Three Column</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div>
                <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>{t('training.accentColor')}</div>
                <input value={slides[editIdx].accent || "00D4FF"} onChange={e => updateSlide(editIdx, "accent", e.target.value.replace("#", ""))} style={inp} placeholder="00D4FF" />
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>{t('training.titleLabel')}</div>
              <input value={slides[editIdx].title} onChange={e => updateSlide(editIdx, "title", e.target.value)} style={inp} />
            </div>

            {(slides[editIdx].type === "title") && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>{t('training.subtitleLabel')}</div>
                <input value={slides[editIdx].subtitle || ""} onChange={e => updateSlide(editIdx, "subtitle", e.target.value)} style={inp} />
              </div>
            )}

            {(slides[editIdx].type === "content" || slides[editIdx].type === "custom") && (
              <>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>{t('training.bodyText')}</div>
                  <textarea value={slides[editIdx].body || ""} onChange={e => updateSlide(editIdx, "body", e.target.value)}
                    style={{ ...inp, minHeight: 70, resize: "vertical", lineHeight: 1.6 }} />
                </div>
                <div>
                  <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 8 }}>{t('training.bulletPoints')}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {(slides[editIdx].bullets || []).map((b, bi) => (
                      <div key={bi} style={{ display: "flex", gap: 6 }}>
                        <input value={b} onChange={e => updateBullet(editIdx, bi, e.target.value)} style={{ ...inp, flex: 1 }} />
                        <button onClick={() => removeBullet(editIdx, bi)}
                          style={{ padding: "0 10px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 5, color: C.danger, cursor: "pointer", fontSize: 12 }}>✕</button>
                      </div>
                    ))}
                    <button onClick={() => addBullet(editIdx)}
                      style={{ padding: "6px", background: "transparent", border: `1px dashed ${C.borderLight}`, borderRadius: 5, color: C.textDim, cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
                      {t('training.addBullet')}
                    </button>
                  </div>
                </div>
              </>
            )}

            {slides[editIdx].type === "three-col" && (
              <div>
                <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 8 }}>{t('training.columns')}</div>
                {(slides[editIdx].cols || [{},{},{}]).map((col, ci) => (
                  <div key={ci} style={{ background: C.bg, borderRadius: 8, padding: 12, marginBottom: 8 }}>
                    <div style={{ color: C.textDim, fontSize: 10, marginBottom: 6 }}>{t('training.columnN', { n: ci + 1 })}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
                      <input value={col.icon || ""} onChange={e => { const cols = [...(slides[editIdx].cols||[{},{},{}])]; cols[ci] = { ...cols[ci], icon: e.target.value }; updateSlide(editIdx, "cols", cols); }} placeholder="Icon/emoji" style={inp} />
                      <input value={col.heading || ""} onChange={e => { const cols = [...(slides[editIdx].cols||[{},{},{}])]; cols[ci] = { ...cols[ci], heading: e.target.value }; updateSlide(editIdx, "cols", cols); }} placeholder="Heading" style={inp} />
                    </div>
                    <textarea value={col.body || ""} onChange={e => { const cols = [...(slides[editIdx].cols||[{},{},{}])]; cols[ci] = { ...cols[ci], body: e.target.value }; updateSlide(editIdx, "cols", cols); }} placeholder="Body text" style={{ ...inp, minHeight: 55, resize: "vertical" }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── QUESTION EDITOR ──────────────────────────────────────────────────────────
function QuestionEditor({ questions, onChange }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [editIdx, setEditIdx] = useState(null);

  function updateQ(idx, field, value) {
    onChange(questions.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  }

  function updateOption(idx, oi, value) {
    onChange(questions.map((q, i) => {
      if (i !== idx) return q;
      const options = [...q.options];
      options[oi] = value;
      return { ...q, options };
    }));
  }

  function addQuestion() {
    onChange([...questions, { q: "New question?", options: ["Option A", "Option B", "Option C", "Option D"], answer: 0, explanation: "" }]);
    setEditIdx(questions.length);
  }

  function removeQuestion(idx) {
    onChange(questions.filter((_, i) => i !== idx));
    setEditIdx(null);
  }

  const inp = { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "7px 10px", fontFamily: "'DM Mono',monospace", outline: "none", width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ display: "flex", gap: 16 }}>
      {/* Question list */}
      <div style={{ width: 220, flexShrink: 0 }}>
        <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", marginBottom: 8 }}>{t('training.questionsCount', { count: questions.length })}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {questions.map((q, i) => (
            <button key={i} onClick={() => setEditIdx(i)}
              style={{ textAlign: "left", padding: "8px 10px", borderRadius: 7, background: editIdx === i ? C.accentGlow : "transparent", border: `1px solid ${editIdx === i ? C.accent : C.border}`, color: editIdx === i ? C.accent : C.textMid, fontSize: 11, fontFamily: "'DM Mono',monospace", cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {i + 1}. {q.q?.slice(0, 36)}{q.q?.length > 36 ? "…" : ""}
            </button>
          ))}
          <button onClick={addQuestion}
            style={{ padding: "8px 10px", borderRadius: 7, background: "transparent", border: `1px dashed ${C.borderLight}`, color: C.textDim, fontSize: 11, cursor: "pointer", fontFamily: "'DM Mono',monospace" }}>
            {t('training.addQuestion')}
          </button>
        </div>
      </div>

      {/* Edit panel */}
      <div style={{ flex: 1 }}>
        {editIdx === null ? (
          <div style={{ color: C.textDim, fontSize: 13, padding: "40px 0", textAlign: "center" }}>{t('training.selectQuestionToEdit')}</div>
        ) : (() => {
          const q = questions[editIdx];
          return (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{t('training.questionN', { n: editIdx + 1 })}</div>
                <button onClick={() => removeQuestion(editIdx)}
                  style={{ padding: "4px 12px", background: "transparent", border: `1px solid ${C.danger}40`, borderRadius: 5, color: C.danger, cursor: "pointer", fontSize: 11 }}>{t('training.delete')}</button>
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>{t('training.questionText')}</div>
                <textarea value={q.q} onChange={e => updateQ(editIdx, "q", e.target.value)}
                  style={{ ...inp, minHeight: 60, resize: "vertical" }} />
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 8 }}>
                  {t('training.answerOptions')} <span style={{ color: C.textDim, fontWeight: 400 }}>({t('training.answerOptionsHint')})</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  {q.options.map((opt, oi) => (
                    <div key={oi} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button onClick={() => updateQ(editIdx, "answer", oi)}
                        style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, cursor: "pointer", background: q.answer === oi ? C.success : "transparent", border: `2px solid ${q.answer === oi ? C.success : C.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {q.answer === oi && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff" }} />}
                      </button>
                      <input value={opt} onChange={e => updateOption(editIdx, oi, e.target.value)}
                        style={{ ...inp, flex: 1, border: `1px solid ${q.answer === oi ? C.success + "60" : C.border}` }} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>{t('training.explanation')}</div>
                <textarea value={q.explanation || ""} onChange={e => updateQ(editIdx, "explanation", e.target.value)}
                  style={{ ...inp, minHeight: 70, resize: "vertical" }} />
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ─── AI ASSISTANT PANEL ───────────────────────────────────────────────────────
function AITrainingPanel({ training, onUpdateSlides, onUpdateQuestions, onRequestSave, company, currentUser }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [messages,  setMessages]  = useState([{ role: "assistant", content: `Hi! I can modify this training module directly. Tell me what you want and I'll do it:\n\n• "Add 12 slides about regulatory requirements with real-life examples"\n• "Rewrite slide 2 to be clearer"\n• "Add a new slide about sanctions"\n• "Beautify all slides — add more structure"\n• "Add 5 quiz questions"\n• "Rewrite the whole module for advanced users"\n\nAfter I make changes, click Save to keep them.` }]);
  const [input,     setInput]     = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const bottomRef = useRef();
  const { callAI } = useAITracker(company, currentUser);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Robustly extract JSON array from AI response regardless of preamble/markdown
  function extractJSON(text) {
    // Strip markdown code fences first
    const stripped = text.replace(/```json\s*/gi, "").replace(/```\s*/g, "").trim();
    // Find outermost JSON array
    const start = stripped.indexOf("[");
    const end   = stripped.lastIndexOf("]");
    if (start !== -1 && end !== -1 && end > start) {
      try { return JSON.parse(stripped.slice(start, end + 1)); } catch {}
    }
    // Fallback: try whole stripped text
    try { return JSON.parse(stripped); } catch {}
    return null;
  }

  function extractJSONObject(text) {
    const stripped = text.replace(/```json\s*/gi, "").replace(/```\s*/g, "").trim();
    const start = stripped.indexOf("{");
    const end   = stripped.lastIndexOf("}");
    if (start !== -1 && end !== -1 && end > start) {
      try { return JSON.parse(stripped.slice(start, end + 1)); } catch {}
    }
    return null;
  }

  function detectAction(response) {
    const upper = response.toUpperCase();
    if (upper.includes("ALL_SLIDES_JSON") || upper.includes("REWRITE_ALL_SLIDES") || upper.includes("BEAUTIFY_SLIDES") || upper.includes("BEAUTIFIED_SLIDES")) return "rewrite_all_slides";
    if (upper.includes("ALL_QUESTIONS_JSON") || upper.includes("REWRITE_ALL_QUESTIONS")) return "rewrite_all_questions";
    if (upper.includes("SLIDES_JSON") || upper.includes("NEW_SLIDES")) return "new_slides";
    if (upper.includes("QUESTIONS_JSON") || upper.includes("NEW_QUESTIONS")) return "new_questions";
    if (upper.includes("REWRITE_SLIDE:") || upper.includes("REWRITE SLIDE")) return "rewrite_slide";
    return "text";
  }

  // For large requests (many slides), split into batches of 6 and make multiple calls
  async function fetchSlidesInBatches(userMsg, systemPrompt, count) {
    const BATCH = 4;
    const batches = Math.ceil(count / BATCH);
    let allSlides = [];
    for (let b = 0; b < batches; b++) {
      const batchMsg = `${userMsg} — Batch ${b + 1} of ${batches}: generate slides ${b * BATCH + 1} to ${Math.min((b + 1) * BATCH, count)} only. Return ONLY the JSON array for this batch, no other text.`;
      const resp = await callAI({
        system: systemPrompt,
        messages: [{ role: "user", content: batchMsg }],
        maxTokens: 4000,
        feature: "ai_assistant",
      });
      const parsed = extractJSON(resp);
      if (parsed && parsed.length > 0) allSlides = [...allSlides, ...parsed];
    }
    return allSlides;
  }

  async function send() {
    if (!input.trim() || aiLoading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(m => [...m, { role: "user", content: userMsg }]);
    setAiLoading(true);

    // Detect if user is asking for many slides (e.g. "12 slides", "10 more slides")
    const manySlides = userMsg.match(/(\d+)\s*(?:more\s+)?slides?/i);
    const requestedCount = manySlides ? parseInt(manySlides[1]) : 0;
    const needsBatching = requestedCount >= 5;

    // Ultra-lean system prompt — maximises output token headroom for JSON
    const systemPrompt = `You are a compliance training designer. Output ONLY a JSON array — zero explanation, zero markdown fences, zero preamble.

Module: "${training.title}" | Slides: ${training.slides.map((s,i)=>`[${i}]${s.title}`).join(", ")}

content slide: {"title":"...","type":"content","body":"1-2 sentences","bullets":["...","...","...","..."],"bg":"111827","accent":"HEXCOLOR"}
three-col slide: {"title":"...","type":"three-col","cols":[{"heading":"...","icon":"⚠","body":"..."},{"heading":"...","icon":"◈","body":"..."},{"heading":"...","icon":"✓","body":"..."}],"bg":"111827","accent":"HEXCOLOR"}
title slide: {"title":"...","subtitle":"...","type":"title","bg":"0A1628","accent":"HEXCOLOR"}
question: {"q":"...","options":["A","B","C","D"],"answer":0,"explanation":"..."}
Colors: 00D4FF 0052FF FF4444 FF8C00 00E676 F0B429 A78BFA

Begin response with the action keyword on line 1, JSON on line 2:
SLIDES_JSON:          ← add new slides
ALL_SLIDES_JSON:      ← replace all slides
REWRITE_SLIDE:N       ← replace slide at 0-based index N
QUESTIONS_JSON:       ← add quiz questions
ALL_QUESTIONS_JSON:   ← replace all questions`;

    try {
      let response;

      if (needsBatching) {
        // Large request — batch it
        setMessages(m => [...m, { role: "assistant", content: `Generating ${requestedCount} slides in batches — this may take 20–40 seconds…`, isAction: false, isInfo: true }]);
        const slides = await fetchSlidesInBatches(userMsg, systemPrompt, requestedCount);
        if (slides.length > 0) {
          onUpdateSlides(prev => [...prev, ...slides]);
          setMessages(m => {
            const filtered = m.filter(msg => !msg.isInfo);
            return [...filtered, { role: "assistant", content: `✓ Added ${slides.length} new slides. Click Save Changes to keep them.`, isAction: true }];
          });
          onRequestSave();
        } else {
          setMessages(m => m.filter(msg => !msg.isInfo).concat([{ role: "assistant", content: "I generated the slides but had trouble parsing them. Try asking for fewer slides at once (e.g. 6 at a time)." }]));
        }
        setAiLoading(false);
        return;
      }

      // Normal single request
      const history = messages.slice(-4).map(m => ({ role: m.role, content: m.content }));
      response = await callAI({
        system: systemPrompt,
        messages: [...history, { role: "user", content: userMsg }],
        maxTokens: 4000,
        feature: "ai_assistant",
      });

      const action = detectAction(response);

      if (action === "new_slides") {
        const parsed = extractJSON(response);
        if (parsed && parsed.length > 0) {
          onUpdateSlides(prev => [...prev, ...parsed]);
          setMessages(m => [...m, { role: "assistant", content: `✓ Added ${parsed.length} new slide${parsed.length !== 1 ? "s" : ""}. Click Save Changes to keep them.`, isAction: true }]);
          onRequestSave();
        } else {
          setMessages(m => [...m, { role: "assistant", content: "I generated slides but couldn't parse the JSON. Try asking for fewer at once (e.g. 4–6 slides)." }]);
        }

      } else if (action === "rewrite_all_slides") {
        const parsed = extractJSON(response);
        if (parsed && parsed.length > 0) {
          onUpdateSlides(parsed);
          setMessages(m => [...m, { role: "assistant", content: `✓ All ${parsed.length} slides rewritten. Click Save Changes to keep them.`, isAction: true }]);
          onRequestSave();
        } else {
          setMessages(m => [...m, { role: "assistant", content: "Couldn't parse the rewritten slides. Try 'Beautify all slides' or ask for fewer changes at once." }]);
        }

      } else if (action === "rewrite_slide") {
        const idxMatch = response.match(/REWRITE_SLIDE:\s*(\d+)/i);
        const idx = idxMatch ? parseInt(idxMatch[1]) : null;
        const afterPrefix = response.replace(/REWRITE_SLIDE:\s*\d+/i, "").trim();
        const parsed = extractJSONObject(afterPrefix);
        if (idx !== null && parsed) {
          onUpdateSlides(prev => prev.map((s, i) => i === idx ? { ...s, ...parsed } : s));
          setMessages(m => [...m, { role: "assistant", content: `✓ Slide ${idx + 1} updated. Click Save Changes to keep it.`, isAction: true }]);
          onRequestSave();
        } else {
          // Fallback: append as new slide
          const parsed2 = extractJSON(response);
          if (parsed2 && parsed2.length > 0) {
            onUpdateSlides(prev => [...prev, ...parsed2]);
            setMessages(m => [...m, { role: "assistant", content: `✓ Added updated slide. Click Save Changes to keep it.`, isAction: true }]);
            onRequestSave();
          } else {
            setMessages(m => [...m, { role: "assistant", content: response }]);
          }
        }

      } else if (action === "new_questions") {
        const parsed = extractJSON(response);
        if (parsed && parsed.length > 0) {
          onUpdateQuestions(prev => [...prev, ...parsed]);
          setMessages(m => [...m, { role: "assistant", content: `✓ Added ${parsed.length} new question${parsed.length !== 1 ? "s" : ""}. Click Save Changes to keep them.`, isAction: true }]);
          onRequestSave();
        } else {
          setMessages(m => [...m, { role: "assistant", content: "Couldn't parse the questions. Please try again." }]);
        }

      } else if (action === "rewrite_all_questions") {
        const parsed = extractJSON(response);
        if (parsed && parsed.length > 0) {
          onUpdateQuestions(parsed);
          setMessages(m => [...m, { role: "assistant", content: `✓ All ${parsed.length} quiz questions rewritten. Click Save Changes to keep them.`, isAction: true }]);
          onRequestSave();
        } else {
          setMessages(m => [...m, { role: "assistant", content: "Couldn't parse the questions. Please try again." }]);
        }

      } else {
        setMessages(m => [...m, { role: "assistant", content: response }]);
      }

    } catch (e) {
      // Show the actual error so you can diagnose it
      const msg = e?.message || String(e);
      setMessages(m => [...m, {
        role: "assistant",
        content: `Something went wrong: ${msg}\n\nIf this was a large request (many slides), try breaking it into smaller batches — e.g. "Add 6 slides about X" then "Add 6 more slides about Y".`,
      }]);
    }
    setAiLoading(false);
  }

  const QUICK_PROMPTS = [
    "Add 6 slides with real-life examples",
    "Add 6 more regulatory slides",
    "Beautify all slides",
    "Add 5 quiz questions",
    "Add a case study slide",
    "Rewrite slide 1",
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 500 }}>
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column",
        gap: 12, padding: "4px 0 12px", marginBottom: 12 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: "88%", padding: "11px 14px",
              borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
              background: m.role === "user" ? C.accentGlow
                        : m.isAction    ? "rgba(0,230,118,0.08)"
                        : m.isInfo      ? C.accentGlow
                        : C.surface,
              border: `1px solid ${m.role === "user" ? C.accent + "60"
                      : m.isAction ? "rgba(0,230,118,0.25)"
                      : m.isInfo   ? C.borderLight
                      : C.border}`,
              color: m.isAction ? C.success : C.text,
              fontSize: 13, lineHeight: 1.65,
              fontFamily: "'DM Mono',monospace", whiteSpace: "pre-wrap" }}>
              {m.content}
            </div>
          </div>
        ))}
        {aiLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.textDim, fontSize: 12 }}>
            <div style={{ display: "flex", gap: 4 }}>
              {[0,1,2].map(i => (
                <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent,
                  animation: `bounce 1s ${i * 0.15}s ease-in-out infinite`, opacity: 0.7 }} />
              ))}
            </div>
            <style>{`@keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}`}</style>
            AI is working…
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        {QUICK_PROMPTS.map(p => (
          <button key={p} onClick={() => setInput(p)}
            style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${C.border}`,
              borderRadius: 14, color: C.textDim, fontSize: 10, cursor: "pointer",
              fontFamily: "'DM Mono',monospace" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accent; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textDim; }}>
            {p}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
          placeholder={t('training.aiPlaceholder')}
          style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
            color: C.text, fontSize: 12, padding: "10px 14px",
            fontFamily: "'DM Mono',monospace", outline: "none" }} />
        <button onClick={send} disabled={aiLoading || !input.trim()}
          style={{ padding: "10px 16px", background: input.trim() ? C.accent : C.border,
            border: "none", borderRadius: 8, color: C.bg, fontWeight: 700,
            cursor: input.trim() ? "pointer" : "default", fontSize: 13 }}>
          {t('training.send')}
        </button>
      </div>
    </div>
  );
}

// ─── PPTX UPLOADER ────────────────────────────────────────────────────────────
function PPTXUploader({ onUpload, company, currentUser }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [dragging,  setDragging]  = useState(false);
  const [parsing,   setParsing]   = useState(false);
  const [error,     setError]     = useState("");
  const [formData,  setFormData]  = useState({ title: "", category: "Custom", description: "", level: "Foundation", duration: "30 min" });
  const fileRef = useRef();
  const { callAI } = useAITracker(company, currentUser);

  async function handleFile(file) {
    if (!file) return;
    if (!file.name.match(/\.(pptx|ppt)$/i)) { setError("Please upload a .pptx or .ppt file."); return; }
    setParsing(true); setError("");

    try {
      // Read as ArrayBuffer and extract text content using basic XML parsing
      const buffer = await file.arrayBuffer();
      const uint8 = new Uint8Array(buffer);

      // PPTX is a ZIP file — use JSZip-style parsing via a simple approach
      // We'll extract text by looking for slide XML patterns
      const decoder = new TextDecoder("utf-8");
      let rawText = "";
      try { rawText = decoder.decode(uint8); } catch { rawText = ""; }

      // Extract readable text segments from the binary (titles and text runs)
      const textMatches = [];
      const xmlRegex = /<a:t[^>]*>([^<]+)<\/a:t>/g;
      let match;
      while ((match = xmlRegex.exec(rawText)) !== null) {
        const t = match[1].trim();
        if (t.length > 2) textMatches.push(t);
      }

      // Group into rough slides (every ~5-10 text items = 1 slide)
      const CHUNK = 8;
      const slideTexts = [];
      for (let i = 0; i < Math.min(textMatches.length, 80); i += CHUNK) {
        slideTexts.push(textMatches.slice(i, i + CHUNK).join(" | "));
      }

      if (slideTexts.length === 0) {
        // Fallback: create a placeholder module and let AI generate content
        slideTexts.push(`${formData.title || file.name.replace(/\.pptx?$/i, "")} — content extracted from uploaded file`);
      }

      // Use AI to convert extracted text into structured slides
      const aiResponse = await callAI({
        system: `You are a training content designer. Convert raw PPTX text extracts into structured training slides in JSON format.
Return ONLY valid JSON array, no other text. Each slide should have:
{"title":"...","type":"content","body":"...","bullets":["...","..."],"bg":"111827","accent":"00D4FF"}
For a title/intro slide use: {"title":"...","subtitle":"...","type":"title","bg":"0A1628","accent":"00D4FF"}
Use accent colors: 00D4FF (blue), FF4444 (red), FF8C00 (orange), 00E676 (green), F0B429 (gold)
Create 6-10 well-structured slides. Make the content educational and professional.`,
        messages: [{
          role: "user",
          content: `Convert this PPTX content into structured training slides.
Title: ${formData.title || file.name.replace(/\.pptx?$/i, "")}
Category: ${formData.category}

Raw text from slides:
${slideTexts.join("\n\n")}

Return a JSON array of slide objects only.`
        }],
        maxTokens: 3000,
        feature: "ai_assistant",
      });

      let slides;
      try {
        const cleaned = aiResponse.replace(/```json?/g, "").replace(/```/g, "").trim();
        slides = JSON.parse(cleaned);
      } catch {
        // Fallback: create basic slides from text
        slides = slideTexts.map((text, i) => ({
          title: i === 0 ? (formData.title || file.name.replace(/\.pptx?$/i, "")) : `Slide ${i + 1}`,
          type: i === 0 ? "title" : "content",
          subtitle: i === 0 ? formData.description : undefined,
          body: i > 0 ? text.split(" | ").slice(0, 2).join(". ") : undefined,
          bullets: i > 0 ? text.split(" | ").slice(2) : undefined,
          bg: i === 0 ? "0A1628" : "111827",
          accent: "00D4FF",
        }));
      }

      // Generate quiz questions from the slides
      const quizResponse = await callAI({
        system: `Generate 5 multiple-choice quiz questions based on training content. Return ONLY valid JSON array:
[{"q":"...","options":["A","B","C","D"],"answer":0,"explanation":"..."}]
answer is the 0-based index of the correct option.`,
        messages: [{
          role: "user",
          content: `Generate 5 quiz questions for this training: ${formData.title}\n\n${slides.map(s => s.title + ": " + (s.body || "") + " " + (s.bullets || []).join(". ")).join("\n")}`
        }],
        maxTokens: 1500,
        feature: "ai_assistant",
      });

      let quiz;
      try {
        const cleaned = quizResponse.replace(/```json?/g, "").replace(/```/g, "").trim();
        quiz = JSON.parse(cleaned);
      } catch {
        quiz = [{ q: "What is the main topic of this training?", options: ["Option A", "Option B", "Option C", "Option D"], answer: 0, explanation: "Review the training slides for the answer." }];
      }

      const newTraining = {
        id: `custom_${Date.now()}`,
        category: formData.category || "Custom",
        title: formData.title || file.name.replace(/\.pptx?$/i, ""),
        subtitle: formData.description,
        duration: formData.duration,
        level: formData.level,
        color: C.purple,
        icon: "◭",
        description: formData.description || `Custom training module uploaded from ${file.name}`,
        isCustom: true,
        slides,
        quiz,
      };

      onUpload(newTraining);
    } catch (e) {
      setError(`Failed to process file: ${e.message}`);
    }
    setParsing(false);
  }

  const inp = { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "7px 10px", fontFamily: "'DM Mono',monospace", outline: "none", width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ maxWidth: 600 }}>
      <h2 style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{t('training.uploadTitle')}</h2>
      <p style={{ color: C.textMid, fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
        {t('training.uploadDesc')}
      </p>

      {/* Module details */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>{t('training.moduleTitle')}</div>
          <input value={formData.title} onChange={e => setFormData(f => ({ ...f, title: e.target.value }))} placeholder={t('training.moduleTitlePlaceholder')} style={inp} />
        </div>
        <div>
          <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>{t('training.category')}</div>
          <select value={formData.category} onChange={e => setFormData(f => ({ ...f, category: e.target.value }))} style={inp}>
            {["AML","Fraud","Compliance","Sanctions","KYC","Custom"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>{t('training.level')}</div>
          <select value={formData.level} onChange={e => setFormData(f => ({ ...f, level: e.target.value }))} style={inp}>
            {["Foundation","Intermediate","Advanced","Expert"].map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>{t('training.duration')}</div>
          <input value={formData.duration} onChange={e => setFormData(f => ({ ...f, duration: e.target.value }))} placeholder={t('training.durationPlaceholder')} style={inp} />
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>{t('training.description')}</div>
          <input value={formData.description} onChange={e => setFormData(f => ({ ...f, description: e.target.value }))} placeholder={t('training.descriptionPlaceholder')} style={inp} />
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => fileRef.current?.click()}
        style={{ border: `2px dashed ${dragging ? C.accent : C.borderLight}`, borderRadius: 12, padding: "40px 20px", textAlign: "center", cursor: "pointer", background: dragging ? C.accentGlow : "transparent", transition: "all 0.15s" }}
      >
        <input ref={fileRef} type="file" accept=".pptx,.ppt" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
        {parsing ? (
          <div style={{ color: C.accent, fontSize: 13, fontFamily: "'DM Mono',monospace" }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>✦</div>
            {t('training.parsingFile')}
            <div style={{ color: C.textDim, fontSize: 11, marginTop: 8 }}>{t('training.parsingTime')}</div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.4 }}>📊</div>
            <div style={{ color: C.text, fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
              {t('training.dropPptx')}
            </div>
            <div style={{ color: C.textDim, fontSize: 12 }}>{t('training.dropPptxHint')}</div>
          </>
        )}
      </div>

      {error && (
        <div style={{ marginTop: 12, background: `${C.danger}12`, border: `1px solid ${C.danger}40`, borderRadius: 8, padding: "10px 14px", color: C.danger, fontSize: 12 }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ─── TEAM ASSIGNMENT + PROGRESS PANEL ────────────────────────────────────────
function TeamProgressPanel({ training, team, company, currentUser }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [assignments, setAssignments] = useState([]);  // training_progress rows for all team members
  const [assigning,   setAssigning]   = useState(false);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    if (!company?.id || !training?.id) return;
    setLoading(true);
    db.from("training_progress")
      .select("*")
      .eq("company_id", company.id)
      .eq("module_id", training.id)
      .then(({ data, error }) => {
        if (error) {
          console.error('[TrainingProgress] Load error:', error.message);
          setAssignments([]);
        } else {
          setAssignments(data || []);
        }
        setLoading(false);
      })
      .catch(e => {
        console.error('[TrainingProgress] Load failed:', e.message);
        setAssignments([]);
        setLoading(false);
      });
  }, [company?.id, training?.id]);

  async function assignMember(member) {
    if (!company?.id) return;
    setAssigning(true);

    try {
      // 1. Upsert training_progress row - try update first, then insert
      const updatePayload = {
        slides_completed: false,
        quiz_passed:      false,
        completed_at:     null,
        updated_at:       new Date().toISOString(),
      };

      const { data: updated, error: updateError } = await db.from("training_progress")
        .update(updatePayload)
        .eq("user_id", member.id)
        .eq("content_id", training.id);

      let row = updated && updated.length > 0 ? updated[0] : null;

      if (!row) {
        // Insert if update found no rows
        const insertPayload = {
          company_id:       company.id,
          user_id:          member.id,
          content_id:       training.id,
          module_id:        training.id,
          slides_completed: false,
          quiz_passed:      false,
          completed_at:     null,
          updated_at:       new Date().toISOString(),
        };
        const { data: inserted, error: insertError } = await db.from("training_progress")
          .insert(insertPayload);

        if (insertError) {
          console.error("[assign] training_progress insert failed:", insertError.message);
          setAssigning(false);
          return;
        }
        row = inserted && inserted.length > 0 ? inserted[0] : null;
      }

      if (row) {
        setAssignments(prev => {
          const exists = prev.find(a => a.user_id === member.id);
          return exists ? prev.map(a => a.user_id === member.id ? row : a) : [...prev, row];
        });

        // 2. Write to unified assignments table (shows on Dashboard)
        // Use check-then-insert to avoid relying on a DB unique constraint
        (async () => {
          try {
            const { data: existing } = await db.from("assignments")
              .select("id")
              .eq("company_id",  company.id)
              .eq("assigned_to", member.id)
              .eq("type",        "training")
              .eq("ref_id",      training.id)
              .maybeSingle();

            if (existing) {
              // Already exists — just reset status to pending in case it was closed
              await db.from("assignments")
                .update({ status: "pending", ref_title: training.title, assigned_by: currentUser?.id || null })
                .eq("id", existing.id);
            } else {
              const { error: insErr } = await db.from("assignments").insert({
                company_id:  company.id,
                assigned_by: currentUser?.id || null,
                assigned_to: member.id,
                type:        "training",
                ref_id:      String(training.id),
                ref_title:   training.title,
                status:      "pending",
              });
              if (insErr) console.error("[assign] assignments insert failed:", insErr.message);
            }
          } catch (e) {
            console.error("[assign] assignments write failed:", e.message);
          }
        })();

        // 3. Send in-app notification
        db.from("notifications").insert({
          company_id:  company.id,
          user_id:     member.id,
          type:        "todo_assigned",
          title:       `Training assigned: ${training.title}`,
          body:        `You have been assigned the "${training.title}" training module. Complete the slides and quiz to earn your certificate.`,
          link_module: "training",
          link_id:     training.id,
        }).catch(e => console.warn("[assign] notification failed:", e.message));
      }
    } catch (e) {
      console.error("[assign] assignMember failed:", e.message);
    } finally {
      setAssigning(false);
    }
  }

  // Team members not yet assigned
  const assignedIds    = new Set(assignments.map(a => a.user_id));
  const unassigned     = (team || []).filter(m => !assignedIds.has(m.id));
  const assignedMembers = (team || []).filter(m => assignedIds.has(m.id));

  const STATUS_COLOR = { done: C.success, in_progress: C.warn, not_started: C.textDim };

  function memberStatus(memberId) {
    const row = assignments.find(a => a.user_id === memberId);
    if (!row) return "not_started";
    if (row.quiz_passed) return "done";
    if (row.slides_completed) return "in_progress";
    return "not_started";
  }

  function statusLabel(s) {
    if (s === "done") return t('training.statusCompleted');
    if (s === "in_progress") return t('training.statusSlidesDone');
    return t('training.statusNotStarted');
  }

  const completedCount = assignedMembers.filter(m => memberStatus(m.id) === "done").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Summary bar */}
      {assignedMembers.length > 0 && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>
              {t('training.teamCompletion')}
            </div>
            <div style={{ color: C.textDim, fontSize: 12 }}>
              {t('training.completedCount', { completed: completedCount, total: assignedMembers.length })}
            </div>
          </div>
          <div style={{ background: C.bg, borderRadius: 4, height: 6, overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: 4, background: C.success,
              width: `${assignedMembers.length > 0 ? Math.round((completedCount / assignedMembers.length) * 100) : 0}%`,
              transition: "width 0.4s" }} />
          </div>
        </div>
      )}

      {/* Assigned members */}
      {loading ? (
        <div style={{ color: C.textDim, fontSize: 12 }}>{t('training.loadingTeam')}</div>
      ) : assignedMembers.length === 0 ? (
        <div style={{ color: C.textDim, fontSize: 13, padding: "20px 0" }}>
          {t('training.noMembersAssigned')}
        </div>
      ) : (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "12px 18px", borderBottom: `1px solid ${C.border}`,
            color: C.textMid, fontSize: 11, letterSpacing: "0.1em" }}>
            {t('training.assignedMembers', { count: assignedMembers.length })}
          </div>
          {assignedMembers.map(m => {
            const status = memberStatus(m.id);
            return (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 14,
                padding: "12px 18px", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                  background: `linear-gradient(135deg, ${C.borderLight}, ${C.border})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: C.textMid, fontSize: 11, fontWeight: 700 }}>
                  {m.firstName?.[0]}{m.lastName?.[0]}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: C.text, fontSize: 13 }}>{m.firstName} {m.lastName}</div>
                  <div style={{ color: C.textDim, fontSize: 11 }}>{m.role}</div>
                </div>
                {/* Progress pills */}
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: assignments.find(a => a.user_id === m.id)?.slides_completed ? C.success : C.textDim,
                    fontFamily: "'DM Mono',monospace" }}>
                    {assignments.find(a => a.user_id === m.id)?.slides_completed ? "✓" : "○"} Slides
                  </span>
                  <span style={{ fontSize: 11, color: assignments.find(a => a.user_id === m.id)?.quiz_passed ? C.success : C.textDim,
                    fontFamily: "'DM Mono',monospace" }}>
                    {assignments.find(a => a.user_id === m.id)?.quiz_passed ? "✓" : "○"} Quiz
                  </span>
                </div>
                <span style={{ color: STATUS_COLOR[status], fontSize: 11, fontFamily: "'DM Mono',monospace",
                  fontWeight: status === "done" ? 700 : 400, minWidth: 90, textAlign: "right" }}>
                  {statusLabel(status)}
                </span>
                {status === "done" && assignments.find(a => a.user_id === m.id)?.completed_at && (
                  <span style={{ color: C.textDim, fontSize: 10, minWidth: 70, textAlign: "right" }}>
                    {new Date(assignments.find(a => a.user_id === m.id).completed_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Assign new members */}
      {unassigned.length > 0 && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "12px 18px", borderBottom: `1px solid ${C.border}`,
            color: C.textMid, fontSize: 11, letterSpacing: "0.1em" }}>
            {t('training.assignToTeam')}
          </div>
          {unassigned.map(m => (
            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 14,
              padding: "10px 18px", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                background: `linear-gradient(135deg, ${C.borderLight}, ${C.border})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: C.textMid, fontSize: 11, fontWeight: 700 }}>
                {m.firstName?.[0]}{m.lastName?.[0]}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: C.text, fontSize: 13 }}>{m.firstName} {m.lastName}</div>
                <div style={{ color: C.textDim, fontSize: 11 }}>{m.role}</div>
              </div>
              <button onClick={() => assignMember(m)} disabled={assigning}
                style={{ padding: "5px 14px", background: C.accentGlow,
                  border: `1px solid ${C.borderLight}`, borderRadius: 6,
                  color: C.accent, fontSize: 11, cursor: "pointer",
                  fontFamily: "'DM Mono',monospace" }}>
                {t('training.assign')}
              </button>
            </div>
          ))}
        </div>
      )}

      {unassigned.length === 0 && assignedMembers.length > 0 && (
        <div style={{ color: C.textDim, fontSize: 12, textAlign: "center", padding: "8px 0" }}>
          {t('training.allAssigned')}
        </div>
      )}
    </div>
  );
}

// ─── TRAINING DETAIL VIEW ─────────────────────────────────────────────────────
function TrainingDetail({ training, progress, onProgressUpdate, onBack, onSave, currentUser, company, team }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [tab,           setTab]           = useState("slides");
  const [editMode,      setEditMode]      = useState(false);
  const [slides,        setSlides]        = useState(training.slides);
  const [questions,     setQuestions]     = useState(training.quiz);
  const [localProgress, setLocalProgress] = useState(progress || {});
  const [saveStatus,    setSaveStatus]    = useState("idle"); // "idle" | "saving" | "saved" | "error"
  const [hasChanges,    setHasChanges]    = useState(false);

  useEffect(() => { setLocalProgress(p => ({ ...p, ...progress })); }, [progress?.slidesCompleted, progress?.quizPassed]);

  // Reset local state when switching training
  useEffect(() => {
    setSlides(training.slides);
    setQuestions(training.quiz);
    setHasChanges(false);
    setSaveStatus("idle");
  }, [training.id]);

  const slidesCompleted = localProgress?.slidesCompleted || false;
  const quizPassed      = localProgress?.quizPassed      || false;
  const fullyComplete   = slidesCompleted && quizPassed;

  function markSlidesComplete() {
    const updated = { ...(progress || {}), ...localProgress, slidesCompleted: true };
    setLocalProgress(updated);
    onProgressUpdate(training.id, updated);
  }

  function markQuizPassed() {
    const updated = { ...(progress || {}), ...localProgress, slidesCompleted: true, quizPassed: true, completedAt: new Date().toISOString() };
    setLocalProgress(updated);
    onProgressUpdate(training.id, updated);
  }

  async function handleSaveChanges() {
    setSaveStatus("saving");
    try {
      await onSave(training.id, { ...training, slides, quiz: questions });
      setHasChanges(false);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2500);
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  }

  function updateSlides(newSlides) {
    const s = typeof newSlides === "function" ? newSlides(slides) : newSlides;
    setSlides(s); setHasChanges(true);
  }

  function updateQuestions(newQs) {
    const q = typeof newQs === "function" ? newQs(questions) : newQs;
    setQuestions(q); setHasChanges(true);
  }

  // Called by AI panel after it makes changes — flags unsaved state
  function onRequestSave() { setHasChanges(true); }

  const TABS = [
    { id: "slides",    label: `📊 ${t('training.tabSlides', { count: slides.length })}`,           done: slidesCompleted },
    { id: "quiz",      label: `✏️ ${t('training.tabQuiz', { count: questions.length })}`,  done: quizPassed },
    ...(editMode ? [
      { id: "edit-slides",    label: `✎ ${t('training.tabEditSlides')}`    },
      { id: "edit-questions", label: `✎ ${t('training.tabEditQuestions')}` },
      { id: "ai",             label: `✦ ${t('training.tabAI')}`   },
      { id: "team",           label: `◍ ${t('training.tabTeam')}`  },
    ] : [
      { id: "team", label: `◍ ${t('training.tabTeam')}` },
    ]),
  ];

  const saveButtonStyle = {
    padding: "7px 18px", border: "none", borderRadius: 7, fontWeight: 700,
    cursor: saveStatus === "saving" ? "wait" : "pointer",
    fontSize: 12, fontFamily: "'DM Mono',monospace",
    background: saveStatus === "saved"  ? C.success
              : saveStatus === "error"  ? C.danger
              : saveStatus === "saving" ? C.borderLight
              : hasChanges              ? C.accent
              : C.surface,
    color: saveStatus === "idle" && !hasChanges ? C.textDim : C.bg,
    border: `1px solid ${hasChanges || saveStatus !== "idle" ? "transparent" : C.border}`,
    transition: "all 0.2s",
  };

  const saveLabel = saveStatus === "saving" ? t('training.saving')
    : saveStatus === "saved"  ? t('training.saved')
    : saveStatus === "error"  ? t('training.saveError')
    : hasChanges              ? t('training.saveChanges')
    : t('training.savedIdle');

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <button onClick={onBack}
          style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6,
            color: C.textMid, cursor: "pointer", padding: "6px 12px", fontSize: 12,
            fontFamily: "'DM Mono',monospace" }}>
          {t('training.backToList')}
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ color: training.color, fontSize: 11, background: `${training.color}18`,
              padding: "2px 8px", borderRadius: 4, fontFamily: "'DM Mono',monospace",
              fontWeight: 700 }}>{training.category}</span>
            {training.isCustom && (
              <span style={{ color: C.purple, fontSize: 11, background: "rgba(167,139,250,0.1)",
                padding: "2px 8px", borderRadius: 4, fontFamily: "'DM Mono',monospace" }}>Custom</span>
            )}
            {fullyComplete && (
              <span style={{ color: C.success, fontSize: 11, background: "rgba(0,230,118,0.1)",
                padding: "2px 8px", borderRadius: 4, fontFamily: "'DM Mono',monospace" }}>{t('training.completedBadge')}</span>
            )}
            {fullyComplete && (
              <button
                onClick={() => downloadCertificate(training.title,
                  `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim(),
                  localProgress?.completedAt, company?.name || currentUser?.role)}
                style={{ background: "transparent", border: `1px solid ${C.accent}`,
                  borderRadius: 6, color: C.accent, padding: "4px 12px", cursor: "pointer",
                  fontSize: 11, fontFamily: "'DM Mono',monospace" }}>
                {t('training.certificate')}
              </button>
            )}
          </div>
          <h2 style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 22,
            fontWeight: 700, margin: "4px 0 0" }}>{training.title}</h2>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Always-visible save button */}
          <button onClick={handleSaveChanges}
            disabled={saveStatus === "saving" || (!hasChanges && saveStatus === "idle")}
            style={saveButtonStyle}>
            {saveLabel}
          </button>
          <button onClick={() => { setEditMode(e => !e); if (editMode) setTab("slides"); }}
            style={{ padding: "7px 16px", background: editMode ? C.accentGlow : "transparent",
              border: `1px solid ${editMode ? C.accent : C.border}`, borderRadius: 7,
              color: editMode ? C.accent : C.textMid, cursor: "pointer", fontSize: 12,
              fontFamily: "'DM Mono',monospace" }}>
            {editMode ? t('training.editing') : t('training.editModule')}
          </button>
          <div style={{ textAlign: "right", color: C.textDim, fontSize: 12 }}>
            {training.duration} · {training.level}
          </div>
        </div>
      </div>

      {/* Unsaved changes banner */}
      {hasChanges && saveStatus === "idle" && (
        <div style={{ background: `${C.warn}10`, border: `1px solid ${C.warn}40`,
          borderRadius: 8, padding: "9px 16px", marginBottom: 14,
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ color: C.warn, fontSize: 12, fontFamily: "'DM Mono',monospace" }}>
            {t('training.unsavedChanges')}
          </span>
          <button onClick={handleSaveChanges}
            style={{ background: C.warn, border: "none", borderRadius: 6, color: C.bg,
              padding: "5px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700,
              fontFamily: "'DM Mono',monospace" }}>
            {t('training.saveNow')}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`,
        marginBottom: 24, gap: 0, overflowX: "auto" }}>
        {TABS.map(tabItem => (
          <button key={tabItem.id} onClick={() => setTab(tabItem.id)}
            style={{ padding: "10px 18px", background: "transparent", border: "none",
              borderBottom: `2px solid ${tab === tabItem.id ? training.color : "transparent"}`,
              color: tab === tabItem.id ? C.text : C.textMid, cursor: "pointer", fontSize: 12,
              fontFamily: "'DM Mono',monospace", display: "flex", alignItems: "center",
              gap: 6, whiteSpace: "nowrap", transition: "all 0.15s" }}>
            {tabItem.label}
            {tabItem.done && <span style={{ color: C.success, fontSize: 11 }}>✓</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "slides" && (
        <SlideViewer slides={slides} onComplete={markSlidesComplete} completed={slidesCompleted} />
      )}

      {tab === "quiz" && (
        <div>
          {!slidesCompleted && (
            <div style={{ background: "rgba(255,140,0,0.08)", border: "1px solid rgba(255,140,0,0.25)",
              borderRadius: 8, padding: "12px 16px", marginBottom: 18, color: C.warn, fontSize: 13 }}>
              {t('training.reviewSlidesWarning')}
            </div>
          )}
          <Quiz questions={questions} onComplete={markQuizPassed} passed={quizPassed} />
        </div>
      )}

      {tab === "edit-slides" && (
        <div>
          <div style={{ color: C.textMid, fontSize: 12, marginBottom: 16 }}>
            {t('training.editSlidesHint')}
          </div>
          <SlideEditor slides={slides} onChange={updateSlides} />
        </div>
      )}

      {tab === "edit-questions" && (
        <div>
          <div style={{ color: C.textMid, fontSize: 12, marginBottom: 16 }}>
            {t('training.editQuestionsHint')}
          </div>
          <QuestionEditor questions={questions} onChange={updateQuestions} />
        </div>
      )}

      {tab === "ai" && (
        <div>
          <div style={{ color: C.textMid, fontSize: 12, marginBottom: 16 }}>
            {t('training.aiHint')}
          </div>
          <AITrainingPanel
            training={{ ...training, slides, quiz: questions }}
            onUpdateSlides={updateSlides}
            onUpdateQuestions={updateQuestions}
            onRequestSave={onRequestSave}
            company={company}
            currentUser={currentUser}
          />
        </div>
      )}

      {tab === "team" && (
        <div>
          <div style={{ color: C.textMid, fontSize: 12, marginBottom: 16 }}>
            {t('training.teamHint')}
          </div>
          <TeamProgressPanel
            training={{ ...training, id: training.id }}
            team={team}
            company={company}
            currentUser={currentUser}
          />
        </div>
      )}
    </div>
  );
}

// ─── TRAINING CARD ────────────────────────────────────────────────────────────
function TrainingCard({ training, progress, onClick }) {
  const C = useTheme();
  const { t } = useTranslation();
  const slidesCompleted = progress?.slidesCompleted || false;
  const quizPassed      = progress?.quizPassed      || false;
  const fullyComplete   = slidesCompleted && quizPassed;
  const pct = fullyComplete ? 100 : slidesCompleted ? 50 : 0;

  return (
    <div onClick={onClick}
      style={{ background: C.surface, border: `1px solid ${fullyComplete ? "rgba(0,230,118,0.3)" : C.border}`, borderRadius: 14, padding: 24, cursor: "pointer", transition: "all 0.15s", position: "relative", overflow: "hidden" }}
      onMouseEnter={e => e.currentTarget.style.borderColor = training.color}
      onMouseLeave={e => e.currentTarget.style.borderColor = fullyComplete ? "rgba(0,230,118,0.3)" : C.border}>

      {fullyComplete && (
        <div style={{ position: "absolute", top: 16, right: 16, width: 28, height: 28, borderRadius: "50%", background: "rgba(0,230,118,0.15)", border: `2px solid ${C.success}`, display: "flex", alignItems: "center", justifyContent: "center", color: C.success, fontSize: 13, fontWeight: 700 }}>✓</div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: `${training.color}18`, border: `1px solid ${training.color}44`, display: "flex", alignItems: "center", justifyContent: "center", color: training.color, fontSize: 20 }}>{training.icon}</div>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ color: training.color, fontSize: 11, fontFamily: "'DM Mono',monospace", fontWeight: 700, letterSpacing: "0.06em" }}>{training.category}</div>
            {training.isCustom && <span style={{ color: C.purple, fontSize: 10, background: "rgba(167,139,250,0.1)", padding: "1px 5px", borderRadius: 3, fontFamily: "'DM Mono',monospace" }}>Custom</span>}
          </div>
          <div style={{ color: C.text, fontSize: 16, fontWeight: 700, fontFamily: "'Syne',sans-serif" }}>{training.title}</div>
        </div>
      </div>

      <div style={{ color: C.textMid, fontSize: 13, lineHeight: 1.6, marginBottom: 18 }}>{training.description}</div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <span style={{ color: C.textDim, fontSize: 11, background: C.bg, padding: "3px 9px", borderRadius: 4, fontFamily: "'DM Mono',monospace" }}>🕐 {training.duration}</span>
        <span style={{ color: C.textDim, fontSize: 11, background: C.bg, padding: "3px 9px", borderRadius: 4, fontFamily: "'DM Mono',monospace" }}>{training.level}</span>
        <span style={{ color: C.textDim, fontSize: 11, background: C.bg, padding: "3px 9px", borderRadius: 4, fontFamily: "'DM Mono',monospace" }}>📊 {training.slides.length} slides</span>
        <span style={{ color: C.textDim, fontSize: 11, background: C.bg, padding: "3px 9px", borderRadius: 4, fontFamily: "'DM Mono',monospace" }}>✏️ {training.quiz.length} questions</span>
      </div>

      <div style={{ background: C.bg, borderRadius: 4, height: 5, marginBottom: 8 }}>
        <div style={{ height: "100%", borderRadius: 4, background: fullyComplete ? C.success : training.color, width: `${pct}%`, transition: "width 0.5s ease" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 12 }}>
          <span style={{ fontSize: 11, color: slidesCompleted ? C.success : C.textDim, fontFamily: "'DM Mono',monospace" }}>{slidesCompleted ? "✓" : "○"} Slides</span>
          <span style={{ fontSize: 11, color: quizPassed ? C.success : C.textDim, fontFamily: "'DM Mono',monospace" }}>{quizPassed ? "✓" : "○"} Quiz</span>
        </div>
        <span style={{ color: fullyComplete ? C.success : training.color, fontSize: 12, fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>
          {fullyComplete ? t('training.cardCompleted') : pct === 0 ? t('training.cardStart') : t('training.cardInProgress')}
        </span>
      </div>
    </div>
  );
}

// ─── MAIN TRAINING PAGE ───────────────────────────────────────────────────────
// All state is Supabase-backed and scoped to company_id.
// • training_modules  — custom uploads + per-company edits to preloaded modules
// • training_progress — per-user, per-company completion records
// Preloaded module content lives in PRELOADED_TRAININGS above as the fallback.
// When a company edits a preloaded module, a row is written with preloaded_id set.
// That row shadows the preloaded default for that company only.

export default function TrainingPage({ currentUser, company, team }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log: auditLog } = useAuditLog(company, currentUser);
  const [dbModules,     setDbModules]     = useState([]);   // rows from training_modules
  const [dbProgress,    setDbProgress]    = useState([]);   // rows from training_progress
  const [loading,       setLoading]       = useState(false);
  const [activeTraining,setActiveTraining]= useState(null);
  const [showUpload,    setShowUpload]    = useState(false);
  const [filter,        setFilter]        = useState("All");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [overdueAlert,  setOverdueAlert]  = useState(false);

  // ── Load company's modules + user's progress from Supabase ─────────────────
  useEffect(() => {
    if (!company?.id || !currentUser?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      db.from("training_modules")
        .select("*")
        .eq("company_id", company.id)
        .order("created_at", true),
      db.from("training_progress")
        .select("*")
        .eq("company_id", company.id)
        .eq("user_id", currentUser.id),
    ]).then(([mods, prog]) => {
      const modsData = mods.data || [];
      const progData = prog.data || [];
      setDbModules(modsData);
      setDbProgress(progData);
      setLoading(false);
      // E-023 / F-017 / E-025: Annual training refresh check (365 days)
      const now = Date.now();
      const oneYearMs = 365 * 24 * 60 * 60 * 1000;
      const progById = {};
      progData.forEach(p => { progById[p.module_id] = p; });
      const overdue = PRELOADED_TRAININGS.filter(tr => {
        const p = progById[tr.id];
        if (!p?.quiz_passed) return true;
        return now - new Date(p.completed_at).getTime() > oneYearMs;
      });
      if (overdue.length > 0) {
        setOverdueAlert(true);
        if (company?.id && currentUser?.id) {
          overdue.forEach(tr => {
            (async () => {
              try {
                const payload = {
                  company_id: company.id,
                  user_id: currentUser.id,
                  type: "training_overdue",
                  title: `Training renewal required: ${tr.title}`,
                  message: `Your "${tr.title}" training is due for annual renewal. Please complete it to maintain compliance.`,
                  read: false,
                };
                await db.from("notifications").insert(payload).catch(() => {});
              } catch (e) {
                console.warn('[Training] Notification create failed:', e.message);
              }
            })();
          });
        }
      }
    }).catch(e => {
      console.error('[Training] Load failed:', e.message);
      setLoading(false);
    });
  }, [company?.id, currentUser?.id]);

  // ── Build the full training list ────────────────────────────────────────────
  // For preloaded modules: check if this company has a saved override in training_modules.
  // If yes, merge the saved slides/quiz onto the preloaded base data.
  // Custom modules (is_custom = true) are appended after preloaded.
  const editedPreloaded = {}; // preloaded_id → db row
  const customModules   = [];
  for (const row of dbModules) {
    if (row.is_custom) {
      customModules.push({
        id:          row.id,
        category:    row.category   || "Custom",
        title:       row.title,
        subtitle:    row.subtitle   || "",
        description: row.description || "",
        duration:    row.duration   || "—",
        level:       row.level      || "Foundation",
        color:       row.color      || C.purple,
        icon:        row.icon       || "◭",
        isCustom:    true,
        slides:      row.slides     || [],
        quiz:        row.quiz       || [],
        _dbId:       row.id,
      });
    } else if (row.preloaded_id) {
      editedPreloaded[row.preloaded_id] = row;
    }
  }

  const allTrainings = [
    ...PRELOADED_TRAININGS.map(tr => {
      const override = editedPreloaded[tr.id];
      if (!override) return tr;
      return {
        ...tr,
        slides:    override.slides || tr.slides,
        quiz:      override.quiz   || tr.quiz,
        _dbId:     override.id,
        _hasEdits: true,
      };
    }),
    ...customModules,
  ];

  // ── Build progress lookup keyed by module id ────────────────────────────────
  const progressMap = {};
  for (const row of dbProgress) {
    progressMap[row.module_id] = {
      slidesCompleted: row.slides_completed,
      quizPassed:      row.quiz_passed,
      completedAt:     row.completed_at,
    };
  }

  const categories     = ["All", ...new Set(allTrainings.map(tr => tr.category))];
  const filtered       = filter === "All" ? allTrainings : allTrainings.filter(tr => tr.category === filter);
  const completedCount = allTrainings.filter(tr => progressMap[tr.id]?.quizPassed).length;

  // ── Update progress in Neon ───────────────────────────────────────────────────
  async function updateProgress(moduleId, data) {
    if (!company?.id || !currentUser?.id) return;
    try {
      const updatePayload = {
        slides_completed: data.slidesCompleted || false,
        quiz_passed:      data.quizPassed      || false,
        completed_at:     data.completedAt     || null,
        updated_at:       new Date().toISOString(),
      };

      // Try update first
      const { data: updated, error: updateError } = await db.from("training_progress")
        .update(updatePayload)
        .eq("user_id", currentUser.id)
        .eq("content_id", moduleId);

      let row = updated && updated.length > 0 ? updated[0] : null;

      // If no rows updated, insert
      if (!row) {
        const insertPayload = {
          company_id:       company.id,
          user_id:          currentUser.id,
          content_id:       moduleId,
          module_id:        moduleId,
          slides_completed: data.slidesCompleted || false,
          quiz_passed:      data.quizPassed      || false,
          completed_at:     data.completedAt     || null,
          updated_at:       new Date().toISOString(),
        };
        const { data: inserted, error: insertError } = await db.from("training_progress")
          .insert(insertPayload);

        if (insertError) {
          // If duplicate key error, try updating instead
          if (insertError.message && insertError.message.includes('unique constraint')) {
            console.log('[TrainingProgress] Duplicate found, updating instead');
            const { data: updated, error: updateError } = await db.from("training_progress")
              .update(updatePayload)
              .eq("user_id", currentUser.id)
              .eq("content_id", moduleId);

            if (updateError) {
              console.error('[TrainingProgress] Update after duplicate error:', updateError.message);
              return;
            }
            row = updated && updated.length > 0 ? updated[0] : null;
            console.log('[TrainingProgress] After duplicate update, row:', row);
          } else {
            console.error('[TrainingProgress] Insert error:', insertError.message);
            return;
          }
        } else {
          row = inserted && inserted.length > 0 ? inserted[0] : null;
          console.log('[TrainingProgress] After insert, row:', row);
        }
      }

      console.log('[TrainingProgress] Row before audit check:', row, 'quizPassed:', data.quizPassed);
      if (row) {
        setDbProgress(prev => {
          const exists = prev.find(p => p.module_id === moduleId);
          return exists ? prev.map(p => p.module_id === moduleId ? row : p) : [...prev, row];
        });
      }

      // Log audit event for quiz completion (regardless of whether row data was returned)
      if (data.quizPassed) {
        try {
          console.log('[Training] Logging quiz completion for:', moduleId, { company: company?.id, user: currentUser?.id });
          await auditLog({
            action: "training_completed",
            module: "training",
            entityId: String(row?.id || moduleId),
            entityName: moduleId,
            meta: { quizPassed: true, moduleName: allTrainings.find(t => t.id === moduleId)?.title }
          });
          console.log('[Training] Quiz completion logged successfully');
        } catch (logErr) {
          console.error('[Training] Failed to log quiz completion:', logErr.message, logErr);
        }
      }
    } catch (e) {
      console.error('[TrainingProgress] Update failed:', e.message);
    }
  }

  // ── Save edited preloaded module ─────────────────────────────────────────────
  // Writes (or upserts) a row to training_modules with preloaded_id set.
  async function savePreloadedEdit(preloadedId, updatedTraining) {
    if (!company?.id) return;
    try {
      const base = PRELOADED_TRAININGS.find(tr => tr.id === preloadedId);
      const existing = editedPreloaded[preloadedId];
      const payload = {
        id:           preloadedId,
        company_id:   company.id,
        created_by:   currentUser.email,
        preloaded_id: preloadedId,
        is_custom:    false,
        title:        updatedTraining.title,
        category:     updatedTraining.category,
        subtitle:     updatedTraining.subtitle || base?.subtitle || "",
        description:  updatedTraining.description || base?.description || "",
        duration:     updatedTraining.duration || base?.duration || "",
        level:        updatedTraining.level || base?.level || "",
        color:        updatedTraining.color || base?.color || "",
        icon:         updatedTraining.icon  || base?.icon  || "",
        slides:       JSON.stringify(updatedTraining.slides),
        quiz:         JSON.stringify(updatedTraining.quiz),
        updated_at:   new Date().toISOString(),
      };
      let row;
      // Check if module exists in database or in edited cache
      const dbExists = dbModules.find(m => m.id === preloadedId);
      if (existing?.id || dbExists) {
        const { data, error } = await db.from("training_modules").update(payload).eq("id", preloadedId);
        if (error) {
          console.error('[Training] Update error:', error.message);
          return;
        }
        row = data && data.length > 0 ? data[0] : null;
      } else {
        const { data, error } = await db.from("training_modules").insert(payload);
        if (error) {
          // If duplicate key error, try updating instead
          if (error.message && error.message.includes('duplicate')) {
            console.log('[Training] Duplicate found, updating instead');
            const { data: updated, error: updateError } = await db.from("training_modules").update(payload).eq("id", preloadedId);
            if (updateError) {
              console.error('[Training] Update after duplicate error:', updateError.message);
              return;
            }
            row = updated && updated.length > 0 ? updated[0] : null;
          } else {
            console.error('[Training] Insert error:', error.message);
            return;
          }
        } else {
          row = data && data.length > 0 ? data[0] : null;
        }
      }
      if (row) {
        try {
          console.log('[Training] Logging training save for:', row.id);
          await auditLog({
            action: "training_saved",
            module: "training",
            entityId: String(row.id),
            entityName: row.title,
            meta: { preloaded: true, preloadedId }
          });
          console.log('[Training] Training save logged successfully');
        } catch (logErr) {
          console.error('[Training] Failed to log training save:', logErr.message);
        }
        setDbModules(prev => {
          const exists = prev.find(r => r.id === row.id);
          return exists ? prev.map(r => r.id === row.id ? row : r) : [...prev, row];
        });
      }
    } catch (e) {
      console.error('[Training] Save failed:', e.message);
    }
  }

  // ── Save custom module ────────────────────────────────────────────────────
  async function saveCustomModule(moduleId, updatedTraining) {
    if (!company?.id) return;
    try {
      const payload = {
        title:       updatedTraining.title,
        category:    updatedTraining.category,
        subtitle:    updatedTraining.subtitle || "",
        description: updatedTraining.description || "",
        duration:    updatedTraining.duration || "",
        level:       updatedTraining.level || "",
        color:       updatedTraining.color || C.purple,
        icon:        updatedTraining.icon  || "◭",
        slides:      JSON.stringify(updatedTraining.slides),
        quiz:        JSON.stringify(updatedTraining.quiz),
        updated_at:  new Date().toISOString(),
      };
      const { data, error } = await db.from("training_modules").update(payload).eq("id", moduleId);
      if (error) {
        console.error('[Training] Update error:', error.message);
        return;
      }
      const row = data && data.length > 0 ? data[0] : null;
      if (row) setDbModules(prev => prev.map(r => r.id === moduleId ? row : r));
    } catch (e) {
      console.error('[Training] Save custom module failed:', e.message);
    }
  }

  // ── Unified save handler called from TrainingDetail ──────────────────────
  async function handleSaveTraining(id, updatedTraining) {
    const isPreloaded = PRELOADED_TRAININGS.find(tr => tr.id === id);
    if (isPreloaded) {
      await savePreloadedEdit(id, updatedTraining);
    } else {
      await saveCustomModule(id, updatedTraining);
    }
    // Refresh active training with latest content
    setActiveTraining({ ...updatedTraining });
  }

  // ── Upload new custom module ──────────────────────────────────────────────
  async function handleUpload(newTraining) {
    if (!company?.id) return;
    try {
      // Generate unique ID for custom training
      const customId = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const payload = {
        id:           customId,
        company_id:   company.id,
        created_by:   currentUser.email,
        preloaded_id: null,
        is_custom:    true,
        title:        newTraining.title,
        category:     newTraining.category,
        subtitle:     newTraining.subtitle     || "",
        description:  newTraining.description  || "",
        duration:     newTraining.duration     || "",
        level:        newTraining.level        || "Foundation",
        color:        newTraining.color        || C.purple,
        icon:         newTraining.icon         || "◭",
        slides:       JSON.stringify(newTraining.slides),
        quiz:         JSON.stringify(newTraining.quiz),
      };
      const { data: rows, error } = await db.from("training_modules").insert(payload);
      if (error) {
        console.error('[Training] Upload error:', error.message);
        return;
      }
      const row = rows && rows.length > 0 ? rows[0] : null;
      if (row) {
        try {
          console.log('[Training] Logging training creation for:', row.id);
          await auditLog({
            action: "training_created",
            module: "training",
            entityId: String(row.id),
            entityName: row.title,
            meta: { custom: true, category: row.category }
          });
          console.log('[Training] Training creation logged successfully');
        } catch (logErr) {
          console.error('[Training] Failed to log training creation:', logErr.message);
        }
        const training = {
          id: row.id, _dbId: row.id, isCustom: true,
          category: row.category, title: row.title, subtitle: row.subtitle,
          description: row.description, duration: row.duration, level: row.level,
          color: row.color || C.purple, icon: row.icon || "◭",
          slides: row.slides ? (typeof row.slides === 'string' ? JSON.parse(row.slides) : row.slides) : [],
          quiz: row.quiz ? (typeof row.quiz === 'string' ? JSON.parse(row.quiz) : row.quiz) : [],
        };
        setDbModules(prev => [...prev, row]);
        setShowUpload(false);
        setActiveTraining(training);
      }
    } catch (e) {
      console.error('[Training] Upload failed:', e.message);
    }
  }

  // ── Delete custom module ──────────────────────────────────────────────────
  async function deleteCustomTraining(dbId) {
    try {
      const training = dbModules.find(m => m.id === dbId);
      const { error } = await db.from("training_modules").delete().eq("id", dbId);
      if (error) {
        console.error('[Training] Delete error:', error.message);
        return;
      }
      if (training) {
        try {
          console.log('[Training] Logging training deletion for:', dbId);
          await auditLog({
            action: "training_deleted",
            module: "training",
            entityId: String(dbId),
            entityName: training.title,
            meta: { custom: true }
          });
          console.log('[Training] Training deletion logged successfully');
        } catch (logErr) {
          console.error('[Training] Failed to log training deletion:', logErr.message);
        }
      }
      setDbModules(prev => prev.filter(r => r.id !== dbId));
      setConfirmDelete(null);
    } catch (e) {
      console.error('[Training] Delete failed:', e.message);
    }
  }

  // ── Reset preloaded module to default ────────────────────────────────────
  async function resetPreloadedTraining(preloadedId) {
    try {
      const row = editedPreloaded[preloadedId];
      if (!row?.id) return;
      const { error } = await db.from("training_modules").delete().eq("id", row.id);
      if (error) {
        console.error('[Training] Reset error:', error.message);
        return;
      }
      setDbModules(prev => prev.filter(r => r.id !== row.id));
    } catch (e) {
      console.error('[Training] Reset failed:', e.message);
    }
  }

  // ── Show upload form ──────────────────────────────────────────────────────
  if (showUpload) {
    return (
      <div>
        <button onClick={() => setShowUpload(false)}
          style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid, cursor: "pointer", padding: "6px 12px", fontSize: 12, fontFamily: "'DM Mono',monospace", marginBottom: 24 }}>
          {t('training.backToList')}
        </button>
        <PPTXUploader onUpload={handleUpload} company={company} currentUser={currentUser} />
      </div>
    );
  }

  // ── Show training detail ──────────────────────────────────────────────────
  if (activeTraining) {
    return (
      <TrainingDetail
        training={activeTraining}
        progress={progressMap[activeTraining.id]}
        onProgressUpdate={updateProgress}
        onBack={() => setActiveTraining(null)}
        onSave={handleSaveTraining}
        currentUser={currentUser}
        company={company}
        team={team}
      />
    );
  }

  async function exportTeamProgressCSV() {
    try {
      // Fetch all team members' training progress (not just current user)
      const { data: allProgress, error } = await db
        .from("training_progress")
        .select("*")
        .eq("company_id", company.id);

      if (error) {
        console.error("Export error:", error.message);
        return;
      }

      const csvData = [];
      csvData.push(["Team Member", "Email", "Training Module", "Status", "Completion %", "Last Updated"]);

      // Get team member info from the team prop
      const teamMap = {};
      if (team && Array.isArray(team)) {
        team.forEach(member => {
          teamMap[member.id] = {
            name: member.name || member.email,
            email: member.email
          };
        });
      }

      // Group progress by user
      const userProgress = {};
      allProgress.forEach(p => {
        if (!userProgress[p.user_id]) {
          const teamMember = teamMap[p.user_id] || { name: "Unknown User", email: p.user_id };
          userProgress[p.user_id] = {
            name: teamMember.name,
            email: teamMember.email,
            progress: {}
          };
        }
        userProgress[p.user_id].progress[p.content_id || p.module_id] = p;
      });

      // Add data for each user
      Object.keys(userProgress).sort((a, b) =>
        userProgress[a].name.localeCompare(userProgress[b].name)
      ).forEach(userId => {
        const user = userProgress[userId];

        allTrainings.forEach(training => {
          const progress = user.progress[training.id];
          let status = "Not Started";
          let progressPercent = "0%";
          let lastUpdated = "";

          if (progress) {
            if (progress.slides_completed && progress.quiz_passed) {
              status = "✓ Completed";
              progressPercent = "100%";
            } else if (progress.slides_completed) {
              status = "In Progress - Quiz Pending";
              progressPercent = "50%";
            } else {
              status = "In Progress - Viewing Slides";
              progressPercent = "25%";
            }
            lastUpdated = progress.updated_at ? new Date(progress.updated_at).toLocaleDateString() : "";
          }

          csvData.push([
            user.name,
            user.email,
            training.title,
            status,
            progressPercent,
            lastUpdated
          ]);
        });
      });

      // Generate CSV
      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Training-Team-Progress-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("CSV export failed:", e.message);
    }
  }

  // ── Training list ──────────────────────────────────────────────────────────
  return (
    <div>
      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@300;400;500&display=swap" rel="stylesheet" />

      {/* Subtle loading indicator — never blocks rendering */}
      {loading && (
        <div style={{ marginBottom: 12, padding: "8px 14px", background: C.accentGlow,
          border: `1px solid ${C.borderLight}`, borderRadius: 7, display: "flex",
          alignItems: "center", gap: 8 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent,
            animation: "crg_pulse 1.2s ease-in-out infinite" }} />
          <style>{`@keyframes crg_pulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
          <span style={{ color: C.textMid, fontSize: 12, fontFamily: "'DM Mono',monospace" }}>
            {t('training.syncing')}
          </span>
        </div>
      )}

      {/* E-023 / E-025: Annual training overdue banner */}
      {overdueAlert && (
        <div style={{ background: "rgba(255,68,68,0.08)", border: "1px solid rgba(255,68,68,0.35)", borderRadius: 8, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ color: "#FF4444", fontSize: 13, fontFamily: "'DM Mono',monospace" }}>
            ⚠ One or more compliance training modules are due for annual renewal. Complete them to maintain your compliance record.
          </div>
          <button onClick={() => setOverdueAlert(false)} style={{ background: "transparent", border: "none", color: "#FF4444", cursor: "pointer", fontSize: 16, padding: "0 4px" }}>×</button>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 26, fontWeight: 700, margin: 0 }}>{t('training.title')}</h1>
          <p style={{ color: C.textMid, margin: "4px 0 0", fontSize: 14 }}>
            {t('training.subtitle', { completed: completedCount, total: allTrainings.length, custom: customModules.length })}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {completedCount === allTrainings.length && allTrainings.length > 0 && (
            <div style={{ background: "rgba(0,230,118,0.08)", border: "1px solid rgba(0,230,118,0.25)", borderRadius: 10, padding: "8px 14px", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: C.success, fontSize: 14 }}>🏆</span>
              <span style={{ color: C.success, fontSize: 12, fontFamily: "'DM Mono',monospace" }}>{t('training.allComplete')}</span>
            </div>
          )}
          <button onClick={exportTeamProgressCSV}
            style={{ padding: "8px 18px", background: "#8B5CF6", border: "none", borderRadius: 8, color: C.bg, fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "'DM Mono',monospace", display: "flex", alignItems: "center", gap: 7 }}>
            ⬇ {t('training.exportCsv', 'Download CSV')}
          </button>
          <button onClick={() => setShowUpload(true)}
            style={{ padding: "8px 18px", background: C.accent, border: "none", borderRadius: 8, color: C.bg, fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "'DM Mono',monospace", display: "flex", alignItems: "center", gap: 7 }}>
            📊 {t('training.uploadPptx')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { label: t('training.statTotal'),       value: allTrainings.length,                                                                                      color: C.accent  },
          { label: t('training.statCompleted'),   value: completedCount,                                                                                           color: C.success },
          { label: t('training.statInProgress'),  value: allTrainings.filter(tr => progressMap[tr.id]?.slidesCompleted && !progressMap[tr.id]?.quizPassed).length, color: C.warn    },
          { label: t('training.statNotStarted'),  value: allTrainings.filter(tr => !progressMap[tr.id]?.slidesCompleted).length,                                   color: C.textDim },
        ].map(s => (
          <div key={s.label} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 18px" }}>
            <div style={{ color: C.textDim, fontSize: 11, letterSpacing: "0.08em", marginBottom: 6 }}>{s.label.toUpperCase()}</div>
            <div style={{ color: s.color, fontSize: 28, fontWeight: 700, fontFamily: "'Syne',sans-serif" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Category filter */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {categories.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ padding: "6px 14px", borderRadius: 7, fontSize: 12, cursor: "pointer", background: filter === f ? C.accentGlow : "transparent", border: `1px solid ${filter === f ? C.accent : C.border}`, color: filter === f ? C.accent : C.textMid, fontFamily: "'DM Mono',monospace" }}>
            {f}
          </button>
        ))}
      </div>

      {/* Training cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 16 }}>
        {filtered.map(tr => (
          <div key={tr.id} style={{ position: "relative" }}>
            <TrainingCard training={tr} progress={progressMap[tr.id]} onClick={() => setActiveTraining(tr)} />

            {/* Custom: delete button */}
            {tr.isCustom && (
              <button
                onClick={e => { e.stopPropagation(); setConfirmDelete(tr); }}
                style={{ position: "absolute", top: 12, left: 12, background: "transparent", border: `1px solid ${C.danger}40`, borderRadius: 5, color: C.danger, fontSize: 10, cursor: "pointer", padding: "2px 7px", fontFamily: "'DM Mono',monospace" }}>
                {t('training.delete')}
              </button>
            )}

            {/* Preloaded + edited: reset button */}
            {tr.isPreloaded && tr._hasEdits && (
              <button
                onClick={e => { e.stopPropagation(); resetPreloadedTraining(tr.id); }}
                style={{ position: "absolute", top: 12, left: 12, background: "transparent", border: `1px solid ${C.warn}40`, borderRadius: 5, color: C.warn, fontSize: 10, cursor: "pointer", padding: "2px 7px", fontFamily: "'DM Mono',monospace" }}>
                {t('training.resetToDefault')}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Confirm delete dialog */}
      {confirmDelete && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "28px 30px", maxWidth: 400, width: "90%", fontFamily: "'DM Mono',monospace" }}>
            <div style={{ color: C.text, fontSize: 16, fontWeight: 700, fontFamily: "'Syne',sans-serif", marginBottom: 10 }}>{t('training.confirmDeleteTitle', { title: confirmDelete.title })}</div>
            <div style={{ color: C.textMid, fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>{t('training.confirmDeleteDesc')}</div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmDelete(null)}
                style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, padding: "8px 18px", cursor: "pointer", fontSize: 13 }}>{t('common.cancel')}</button>
              <button onClick={() => deleteCustomTraining(confirmDelete._dbId || confirmDelete.id)}
                style={{ background: C.danger, border: "none", borderRadius: 7, color: "#fff", padding: "8px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{t('training.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}